import type {
  BaseMetadata,
  BaseUserMeta,
  BroadcastOptions,
  Client,
  CommentData,
  FeedCreateMetadata,
  FeedUpdateMetadata,
  History,
  Json,
  JsonObject,
  LiveObject,
  LostConnectionEvent,
  LsonObject,
  OthersEvent,
  Room,
  Status,
  ThreadData,
  User,
} from "@liveblocks/client";
import { shallow } from "@liveblocks/client";
import type {
  AsyncResult,
  CommentsEventServerMsg,
  DCM,
  DE,
  DFM,
  DFMD,
  DP,
  DS,
  DTM,
  DU,
  EnterOptions,
  FeedsEventServerMsg,
  IYjsProvider,
  LiveblocksErrorContext,
  MentionData,
  OpaqueClient,
  OpaqueRoom,
  PermissionResources,
  RequiredAccessLevel,
  RoomEventMessage,
  RoomSubscriptionSettings,
  SignalType,
  StorageNode,
  TextEditorType,
  ToJson,
  UnsubscribeCallback,
} from "@liveblocks/core";
import {
  assert,
  console,
  createCommentId,
  createThreadId,
  DefaultMap,
  errorIf,
  getSubscriptionKey,
  hasPermissionAccess,
  HttpError,
  kInternal,
  makePoller,
  ServerMsgCode,
  stableStringify,
} from "@liveblocks/core";
import type { Context } from "react";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  version as reactVersion,
} from "react";

import { config } from "./config";
import {
  GlobalRoomContext,
  useClient,
  useIsInsideRoom,
  useRoomOrNull,
} from "./contexts";
import { ensureNotServerSide } from "./lib/ssr";
import { useInitial } from "./lib/use-initial";
import { useLatest } from "./lib/use-latest";
import { use } from "./lib/use-polyfill";
import {
  createSharedContext,
  getUmbrellaStoreForClient,
  LiveblocksProviderWithClient,
} from "./liveblocks";
import type {
  AttachmentUrlAsyncResult,
  CommentReactionOptions,
  CreateCommentOptions,
  CreateThreadOptions,
  DeleteCommentOptions,
  EditCommentMetadataOptions,
  EditCommentOptions,
  EditThreadMetadataOptions,
  FeedMessagesAsyncResult,
  FeedMessagesAsyncSuccess,
  FeedsAsyncResult,
  FeedsAsyncSuccess,
  HistoryVersionsAsyncResult,
  HistoryVersionsAsyncSuccess,
  HistoryVersionStorageDataAsyncResult,
  HistoryVersionYjsDataAsyncResult,
  MutationContext,
  OmitFirstArg,
  RoomContextBundle,
  RoomProviderProps,
  RoomSubscriptionSettingsAsyncResult,
  RoomSubscriptionSettingsAsyncSuccess,
  SearchCommentsAsyncResult,
  ThreadsAsyncResult,
  ThreadsAsyncSuccess,
  ThreadSubscription,
  UseFeedMessagesOptions,
  UseFeedsOptions,
  UseSearchCommentsOptions,
  UseThreadsOptions,
} from "./types";
import type { UmbrellaStore } from "./umbrella-store";
import {
  makeFeedMessagesQueryKey,
  makeFeedsQueryKey,
  makeRoomThreadsQueryKey,
} from "./umbrella-store";
import { useScrollToCommentOnLoadEffect } from "./use-scroll-to-comment-on-load-effect";
import { useSignal } from "./use-signal";
import { useSyncExternalStoreWithSelector } from "./use-sync-external-store-with-selector";

const noop = () => {};
const identity: <T>(x: T) => T = (x) => x;

const STABLE_EMPTY_LIST = Object.freeze([]);

// Don't try to inline this. This function is intended to be a stable
// reference, to avoid a useCallback() wrapper.
function alwaysEmptyList() {
  return STABLE_EMPTY_LIST;
}

// Don't try to inline this. This function is intended to be a stable
// reference, to avoid a useCallback() wrapper.
function alwaysNull() {
  return null;
}

function selectorFor_useOthersConnectionIds(
  others: readonly User<JsonObject, BaseUserMeta>[]
): number[] {
  return others.map((user) => user.connectionId);
}

function makeMutationContext<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
>(room: Room<P, S, U, E, TM, CM>): MutationContext<P, S, U> {
  const cannotUseUntil = "This mutation cannot be used until";
  const needsPresence = `${cannotUseUntil} connected to the Liveblocks room`;
  const needsStorage = `${cannotUseUntil} storage has been loaded`;

  return {
    get storage() {
      const mutableRoot = room.getStorageOrNull();
      if (mutableRoot === null) {
        throw new Error(needsStorage);
      }
      return mutableRoot;
    },

    get self() {
      const self = room.getSelf();
      if (self === null) {
        throw new Error(needsPresence);
      }
      return self;
    },

    get others() {
      const others = room.getOthers();
      if (room.getSelf() === null) {
        throw new Error(needsPresence);
      }
      return others;
    },

    setMyPresence: room.updatePresence,
  };
}

function getCurrentUserId(client: Client): string {
  const userId = client[kInternal].currentUserId.get();
  if (userId === undefined) {
    return "anonymous";
  }
  return userId;
}

const _extras = new WeakMap<
  OpaqueClient,
  ReturnType<typeof makeRoomExtrasForClient>
>();

// TODO: Likely a better / more clear name for this helper will arise. I'll
// rename this later. All of these are implementation details to support inbox
// notifications on a per-client basis.
function getRoomExtrasForClient<
  TM extends BaseMetadata,
  CM extends BaseMetadata,
>(client: OpaqueClient) {
  let extras = _extras.get(client);
  if (!extras) {
    extras = makeRoomExtrasForClient(client);
    _extras.set(client, extras);
  }

  return extras as unknown as Omit<typeof extras, "store"> & {
    store: UmbrellaStore<TM, CM>;
  };
}

function getThreadVisibility<TM extends BaseMetadata, CM extends BaseMetadata>(
  store: UmbrellaStore<TM, CM>,
  threadId: string
): ThreadData<TM, CM>["visibility"] | undefined {
  return store.outputs.threads.get().getEvenIfDeleted(threadId)?.visibility;
}

function makeRoomExtrasForClient(client: OpaqueClient) {
  const store = getUmbrellaStoreForClient(client);

  function onMutationFailure(
    optimisticId: string,
    context: LiveblocksErrorContext & { roomId: string },
    innerError: Error
  ): void {
    store.optimisticUpdates.remove(optimisticId);

    // All mutation failures are expected to be HTTP errors ultimately - only
    // ever notify the user about those.
    if (innerError instanceof HttpError) {
      // Always log details about 403 Forbidden errors to the console as well
      if (innerError.status === 403) {
        const detailedMessage = [
          innerError.message,
          innerError.details?.suggestion,
          innerError.details?.docs,
        ]
          .filter(Boolean)
          .join("\n");

        console.error(detailedMessage);
      }

      client[kInternal].emitError(context, innerError);
    } else {
      // In this context, a non-HTTP error is unexpected and should be
      // considered a bug we should get fixed. Don't notify the user about it.
      throw innerError;
    }
  }

  const threadsPollersByRoomId = new DefaultMap((roomId: string) =>
    makePoller(
      async (signal) => {
        try {
          return await store.fetchRoomThreadsDeltaUpdate(roomId, signal);
        } catch (err) {
          console.warn(`Polling new threads for '${roomId}' failed: ${String(err)}`); // prettier-ignore
          throw err;
        }
      },
      config.ROOM_THREADS_POLL_INTERVAL,
      { maxStaleTimeMs: config.ROOM_THREADS_MAX_STALE_TIME }
    )
  );

  const versionsPollersByRoomId = new DefaultMap((roomId: string) =>
    makePoller(
      async (signal) => {
        try {
          return await store.fetchRoomVersionsDeltaUpdate(roomId, signal);
        } catch (err) {
          console.warn(`Polling new history versions for '${roomId}' failed: ${String(err)}`); // prettier-ignore
          throw err;
        }
      },
      config.HISTORY_VERSIONS_POLL_INTERVAL,
      { maxStaleTimeMs: config.HISTORY_VERSIONS_MAX_STALE_TIME }
    )
  );

  const roomSubscriptionSettingsPollersByRoomId = new DefaultMap(
    (roomId: string) =>
      makePoller(
        async (signal) => {
          try {
            return await store.refreshRoomSubscriptionSettings(roomId, signal);
          } catch (err) {
            console.warn(`Polling subscription settings for '${roomId}' failed: ${String(err)}`); // prettier-ignore
            throw err;
          }
        },
        config.ROOM_SUBSCRIPTION_SETTINGS_POLL_INTERVAL,
        { maxStaleTimeMs: config.ROOM_SUBSCRIPTION_SETTINGS_MAX_STALE_TIME }
      )
  );

  return {
    store,
    onMutationFailure,
    pollThreadsForRoomId: (roomId: string) => {
      const threadsPoller = threadsPollersByRoomId.getOrCreate(roomId);

      // If there's a threads poller for this room, immediately trigger it
      if (threadsPoller) {
        threadsPoller.markAsStale();
        threadsPoller.pollNowIfStale();
      }
    },
    getOrCreateThreadsPollerForRoomId: threadsPollersByRoomId.getOrCreate.bind(
      threadsPollersByRoomId
    ),
    getOrCreateVersionsPollerForRoomId:
      versionsPollersByRoomId.getOrCreate.bind(versionsPollersByRoomId),
    getOrCreateSubscriptionSettingsPollerForRoomId:
      roomSubscriptionSettingsPollersByRoomId.getOrCreate.bind(
        roomSubscriptionSettingsPollersByRoomId
      ),
  };
}

type RoomLeavePair<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
  FM extends Json = Json,
  FMD extends Json = Json,
> = {
  room: Room<P, S, U, E, TM, CM, FM, FMD>;
  leave: () => void;
};

function RoomProvider<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
  FM extends Json = Json,
  FMD extends Json = Json,
>(
  props: RoomProviderProps<P, S> & {
    /** @internal */
    BoundRoomContext: Context<OpaqueRoom | null>;
  }
) {
  const client = useClient<U>();
  const [cache] = useState(
    () => new Map<string, RoomLeavePair<P, S, U, E, TM, CM, FM, FMD>>()
  );

  // Produce a version of client.enterRoom() that when called for the same
  // room ID multiple times, will not keep producing multiple leave
  // functions, but instead return the cached one.
  const stableEnterRoom: typeof client.enterRoom<P, S, E, TM, CM, FM, FMD> =
    useCallback(
      (
        roomId: string,
        options: EnterOptions<P, S>
      ): RoomLeavePair<P, S, U, E, TM, CM, FM, FMD> => {
        const cached = cache.get(roomId);
        if (cached) return cached;

        const rv = client.enterRoom<P, S, E, TM, CM, FM, FMD>(roomId, options);

        // Wrap the leave function to also delete the cached value
        const origLeave = rv.leave;
        rv.leave = () => {
          origLeave();
          cache.delete(roomId);
        };

        cache.set(roomId, rv);
        return rv;
      },
      [client, cache]
    );

  //
  // RATIONALE:
  // At the "Outer" RoomProvider level, we keep a cache and produce
  // a stableEnterRoom function, which we pass down to the real "Inner"
  // RoomProvider level.
  //
  // The purpose is to ensure that if `stableEnterRoom("my-room")` is called
  // multiple times for the same room ID, it will always return the exact same
  // (cached) value, so that in total only a single "leave" function gets
  // produced and registered in the client.
  //
  // If we didn't use this cache, then in React StrictMode
  // stableEnterRoom("my-room") might get called multiple (at least 4) times,
  // causing more leave functions to be produced in the client, some of which
  // we cannot get a hold on (because StrictMode would discard those results by
  // design). This would make it appear to the Client that the Room is still in
  // use by some party that hasn't called `leave()` on it yet, thus causing the
  // Room to not be freed and destroyed when the component unmounts later.
  //
  return (
    <RoomProviderInner<P, S, U, E, TM, CM, FM, FMD>
      {...(props as any)}
      stableEnterRoom={stableEnterRoom}
    />
  );
}

type EnterRoomType<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
  FM extends Json = Json,
  FMD extends Json = Json,
> = (
  roomId: string,
  options: EnterOptions<P, S>
) => RoomLeavePair<P, S, U, E, TM, CM, FM, FMD>;

/** @internal */
function RoomProviderInner<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
  FM extends Json = Json,
  FMD extends Json = Json,
>(
  props: RoomProviderProps<P, S> & {
    stableEnterRoom: EnterRoomType<P, S, U, E, TM, CM, FM, FMD>;
    BoundRoomContext?: Context<OpaqueRoom | null>;
  }
) {
  const client = useClient<U>();
  const { id: roomId, stableEnterRoom, BoundRoomContext } = props;

  if (process.env.NODE_ENV !== "production") {
    if (!roomId) {
      throw new Error(
        "RoomProvider id property is required. For more information: https://liveblocks.io/docs/errors/liveblocks-react/RoomProvider-id-property-is-required"
      );
    }

    if (typeof roomId !== "string") {
      throw new Error("RoomProvider id property should be a string.");
    }

    const majorReactVersion = parseInt(reactVersion) || 1;
    const requiredVersion = 18;
    errorIf(
      majorReactVersion < requiredVersion,
      `React ${requiredVersion} or higher is required (you’re on ${reactVersion})`
    );
  }

  // Note: We'll hold on to the initial value given here, and ignore any
  // changes to this argument in subsequent renders, except when roomId changes
  const frozenProps = useInitial(
    {
      initialPresence: props.initialPresence,
      initialStorage: props.initialStorage,
      autoConnect: props.autoConnect ?? typeof window !== "undefined",
    },
    roomId
  ) as EnterOptions<P, S>;

  const [{ room }, setRoomLeavePair] = useState(() =>
    stableEnterRoom(roomId, {
      ...frozenProps,
      autoConnect: false, // Deliberately using false here on the first render, see below
    })
  );

  useEffect(() => {
    const { store } = getRoomExtrasForClient(client);

    async function handleCommentEvent(message: CommentsEventServerMsg) {
      // If thread deleted event is received, we remove the thread from the local cache
      // no need for more processing
      if (message.type === ServerMsgCode.THREAD_DELETED) {
        store.deleteThread(message.threadId, null);
        return;
      }

      // TODO: Error handling
      const info = await room.getThread(message.threadId);

      // If no thread info was returned (i.e., 404), we remove the thread and relevant inbox notifications from local cache.
      if (!info.thread) {
        store.deleteThread(message.threadId, null);
        return;
      }
      const {
        thread,
        inboxNotification: maybeNotification,
        subscription: maybeSubscription,
      } = info;

      const existingThread = store.outputs.threads
        .get()
        .getEvenIfDeleted(message.threadId);

      switch (message.type) {
        case ServerMsgCode.COMMENT_EDITED:
        case ServerMsgCode.THREAD_METADATA_UPDATED:
        case ServerMsgCode.THREAD_UPDATED:
        case ServerMsgCode.COMMENT_REACTION_ADDED:
        case ServerMsgCode.COMMENT_REACTION_REMOVED:
        case ServerMsgCode.COMMENT_DELETED:
        case ServerMsgCode.COMMENT_METADATA_UPDATED:
          // If the thread doesn't exist in the local cache, we do not update it with the server data as an optimistic update could have deleted the thread locally.
          if (!existingThread) break;

          store.updateThreadifications(
            [thread],
            maybeNotification ? [maybeNotification] : [],
            maybeSubscription ? [maybeSubscription] : []
          );
          break;

        case ServerMsgCode.COMMENT_CREATED:
          store.updateThreadifications(
            [thread],
            maybeNotification ? [maybeNotification] : [],
            maybeSubscription ? [maybeSubscription] : []
          );
          break;
        default:
          break;
      }
    }

    return room.events.comments.subscribe(
      (message) => void handleCommentEvent(message)
    );
  }, [client, room]);

  useEffect(() => {
    const { store } = getRoomExtrasForClient(client);

    function handleFeedEvent(message: FeedsEventServerMsg): void {
      switch (message.type) {
        case ServerMsgCode.FEEDS_ADDED:
        case ServerMsgCode.FEEDS_UPDATED:
          store.upsertFeeds(room.id, message.feeds);
          break;
        case ServerMsgCode.FEED_DELETED:
          store.deleteFeed(room.id, message.feedId);
          break;
        case ServerMsgCode.FEED_MESSAGES_ADDED:
        case ServerMsgCode.FEED_MESSAGES_UPDATED:
          store.upsertFeedMessages(room.id, message.feedId, message.messages);
          break;
        case ServerMsgCode.FEED_MESSAGES_DELETED:
          store.deleteFeedMessages(room.id, message.feedId, message.messageIds);
          break;
        // FEEDS_LIST and FEED_MESSAGES_LIST are handled by fetch promise resolution in room.ts
        default:
          break;
      }
    }

    return room.events.feeds.subscribe(
      (message: FeedsEventServerMsg) => void handleFeedEvent(message)
    );
  }, [client, room]);

  useEffect(() => {
    const pair = stableEnterRoom(roomId, frozenProps);

    setRoomLeavePair(pair);
    const { room, leave } = pair;

    // In React, it's important to start connecting to the room as an effect,
    // rather than doing this during the initial render. This means that
    // during the initial render (both on the server-side, and on the first
    // hydration on the client-side), the value of the `useStatus()` hook
    // will correctly be "initial", and transition to "connecting" as an
    // effect.
    if (frozenProps.autoConnect) {
      room.connect();
    }

    return () => {
      leave();
    };
  }, [roomId, frozenProps, stableEnterRoom]);

  return (
    <GlobalRoomContext.Provider value={room}>
      {BoundRoomContext ? (
        <BoundRoomContext.Provider value={room}>
          {props.children}
        </BoundRoomContext.Provider>
      ) : (
        props.children
      )}
    </GlobalRoomContext.Provider>
  );
}

/**
 * @internal
 */
function useRoom_withRoomContext<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(
  RoomContext: Context<OpaqueRoom | null>,
  options?: { allowOutsideRoom: false }
): Room<P, S, U, E, TM, CM, FM, FMD>;
function useRoom_withRoomContext<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(
  RoomContext: Context<OpaqueRoom | null>,
  options?: { allowOutsideRoom: boolean }
): Room<P, S, U, E, TM, CM, FM, FMD> | null;
function useRoom_withRoomContext<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(
  RoomContext: Context<OpaqueRoom | null>,
  options?: { allowOutsideRoom: boolean }
): Room<P, S, U, E, TM, CM, FM, FMD> | null {
  const room = useRoomOrNull<P, S, U, E, TM, CM, FM, FMD>(RoomContext);

  if (room === null && !options?.allowOutsideRoom) {
    throw new Error("RoomProvider is missing from the React tree.");
  }

  return room;
}

function useRoom<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(options?: { allowOutsideRoom: false }): Room<P, S, U, E, TM, CM, FM, FMD>;
function useRoom<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(options: {
  allowOutsideRoom: boolean;
}): Room<P, S, U, E, TM, CM, FM, FMD> | null;
function useRoom<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(options?: {
  allowOutsideRoom: boolean;
}): Room<P, S, U, E, TM, CM, FM, FMD> | null {
  return useRoom_withRoomContext<P, S, U, E, TM, CM, FM, FMD>(
    GlobalRoomContext,
    options
  );
}

/**
 * @internal
 */
function useStatus_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): Status {
  const room = useRoom_withRoomContext(RoomContext);
  const subscribe = room.events.status.subscribe;
  const getSnapshot = room.getStatus;
  const getServerSnapshot = room.getStatus;
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Returns the current connection status for the Room, and triggers
 * a re-render whenever it changes. Can be used to render a status badge.
 */
function useStatus(): Status {
  return useStatus_withRoomContext(GlobalRoomContext);
}

/** @private - Internal API, do not rely on it. */
function useReportTextEditor(editor: TextEditorType, rootKey: string): void {
  const isReported = useRef<boolean>(false);
  const room = useRoom();

  useEffect(() => {
    // We use a "locker" reference to avoid to spam / harass our backend
    // and to not add / remove subscribers in case when the text editor type
    // has been already reported.
    if (isReported.current) {
      return;
    }

    const unsubscribe = room.events.status.subscribe((status: Status): void => {
      if (status === "connected" && !isReported.current) {
        isReported.current = true;
        // We do not catch because this method never throw (e.g `rawPost`)
        void room[kInternal].reportTextEditor(editor, rootKey);
      }
    });

    return unsubscribe;
  }, [room, editor, rootKey]);
}

/** @private - Internal API, do not rely on it. */
function useYjsProvider(): IYjsProvider | undefined {
  const room = useRoom();

  const subscribe = useCallback(
    (onStoreChange: () => void): UnsubscribeCallback => {
      return room[kInternal].yjsProviderDidChange.subscribe(onStoreChange);
    },
    [room]
  );

  const getSnapshot = useCallback((): IYjsProvider | undefined => {
    return room[kInternal].getYjsProvider();
  }, [room]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** @private - Internal API, do not rely on it. */
function useCreateTextMention(): (
  mentionId: string,
  mention: MentionData
) => void {
  const room = useRoom();
  return useCallback(
    (mentionId: string, mention: MentionData): void => {
      room[kInternal]
        .createTextMention(mentionId, mention)
        .catch((err): void => {
          console.error(
            `Cannot create text mention for mention '${mentionId}'`,
            err
          );
        });
    },
    [room]
  );
}

/** @private - Internal API, do not rely on it. */
function useDeleteTextMention(): (mentionId: string) => void {
  const room = useRoom();
  return useCallback(
    (mentionId: string): void => {
      room[kInternal].deleteTextMention(mentionId).catch((err): void => {
        console.error(`Cannot delete text mention '${mentionId}'`, err);
      });
    },
    [room]
  );
}

/** @private - Internal API, do not rely on it. */
function useResolveMentionSuggestions() {
  const client = useClient();
  return client[kInternal].resolveMentionSuggestions;
}

/** @private - Internal API, do not rely on it. */
function useMentionSuggestionsCache() {
  const client = useClient();
  return client[kInternal].mentionSuggestionsCache;
}

/**
 * @internal
 */
function useBroadcastEvent_withRoomContext<E extends Json>(
  RoomContext: Context<OpaqueRoom | null>
): (event: E, options?: BroadcastOptions) => void {
  const room = useRoom_withRoomContext<never, never, never, E, never, never>(
    RoomContext
  );
  return useCallback(
    (
      event: E,
      options: BroadcastOptions = { shouldQueueEventIfNotReady: false }
    ) => {
      room.broadcastEvent(event, options);
    },
    [room]
  );
}

function useBroadcastEvent<E extends Json>(): (
  event: E,
  options?: BroadcastOptions
) => void {
  return useBroadcastEvent_withRoomContext<E>(GlobalRoomContext);
}

/**
 * @internal
 */
function useOthersListener_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
>(
  RoomContext: Context<OpaqueRoom | null>,
  callback: (event: OthersEvent<P, U>) => void
) {
  const room = useRoom_withRoomContext<P, never, U, never, never, never>(
    RoomContext
  );
  const savedCallback = useLatest(callback);
  useEffect(
    () => room.events.others.subscribe((event) => savedCallback.current(event)),
    [room, savedCallback]
  );
}

function useOthersListener<P extends JsonObject, U extends BaseUserMeta>(
  callback: (event: OthersEvent<P, U>) => void
) {
  return useOthersListener_withRoomContext<P, U>(GlobalRoomContext, callback);
}

/**
 * @internal
 */
function useLostConnectionListener_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  callback: (event: LostConnectionEvent) => void
): void {
  const room = useRoom_withRoomContext(RoomContext);
  const savedCallback = useLatest(callback);
  useEffect(
    () =>
      room.events.lostConnection.subscribe((event) =>
        savedCallback.current(event)
      ),
    [room, savedCallback]
  );
}

/**
 * Get informed when reconnecting to the Liveblocks servers is taking
 * longer than usual. This typically is a sign of a client that has lost
 * internet connectivity.
 *
 * This isn't problematic (because the Liveblocks client is still trying to
 * reconnect), but it's typically a good idea to inform users about it if
 * the connection takes too long to recover.
 *
 * @example
 * useLostConnectionListener(event => {
 *   if (event === 'lost') {
 *     toast.warn('Reconnecting to the Liveblocks servers is taking longer than usual...')
 *   } else if (event === 'failed') {
 *     toast.warn('Reconnecting to the Liveblocks servers failed.')
 *   } else if (event === 'restored') {
 *     toast.clear();
 *   }
 * })
 */
function useLostConnectionListener(
  callback: (event: LostConnectionEvent) => void
): void {
  return useLostConnectionListener_withRoomContext(GlobalRoomContext, callback);
}

/**
 * @internal
 */
function useEventListener_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  E extends Json,
>(
  RoomContext: Context<OpaqueRoom | null>,
  callback: (data: RoomEventMessage<P, U, E>) => void
): void {
  const room = useRoom_withRoomContext<P, never, U, E, never, never>(
    RoomContext
  );
  const savedCallback = useLatest(callback);
  useEffect(() => {
    const listener = (eventData: RoomEventMessage<P, U, E>) => {
      savedCallback.current(eventData);
    };

    return room.events.customEvent.subscribe(listener);
  }, [room, savedCallback]);
}

function useEventListener<
  P extends JsonObject,
  U extends BaseUserMeta,
  E extends Json,
>(callback: (data: RoomEventMessage<P, U, E>) => void): void {
  return useEventListener_withRoomContext<P, U, E>(GlobalRoomContext, callback);
}

/**
 * @internal
 */
function useHistory_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): History {
  return useRoom_withRoomContext(RoomContext).history;
}

/**
 * Returns the room.history
 */
function useHistory(): History {
  return useHistory_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useUndo_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): () => void {
  return useHistory_withRoomContext(RoomContext).undo;
}

/**
 * Returns a function that undoes the last operation executed by the current
 * client. It does not impact operations made by other clients.
 */
function useUndo(): () => void {
  return useUndo_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useRedo_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): () => void {
  return useHistory_withRoomContext(RoomContext).redo;
}

/**
 * Returns a function that redoes the last operation executed by the current
 * client. It does not impact operations made by other clients.
 */
function useRedo(): () => void {
  return useRedo_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useCanUndo_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): boolean {
  const room = useRoom_withRoomContext(RoomContext);
  const subscribe = room.events.history.subscribe;
  const canUndo = room.history.canUndo;
  return useSyncExternalStore(subscribe, canUndo, canUndo);
}

/**
 * Returns whether there are any operations to undo.
 */
function useCanUndo(): boolean {
  return useCanUndo_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useCanRedo_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): boolean {
  const room = useRoom_withRoomContext(RoomContext);
  const subscribe = room.events.history.subscribe;
  const canRedo = room.history.canRedo;
  return useSyncExternalStore(subscribe, canRedo, canRedo);
}

/**
 * Returns whether there are any operations to redo.
 */
function useCanRedo(): boolean {
  return useCanRedo_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useSelf_withRoomContext<P extends JsonObject, U extends BaseUserMeta>(
  RoomContext: Context<OpaqueRoom | null>
): User<P, U> | null;
function useSelf_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector: (me: User<P, U>) => T,
  isEqual?: (prev: T | null, curr: T | null) => boolean
): T | null;
function useSelf_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  maybeSelector?: (me: User<P, U>) => T,
  isEqual?: (prev: T | null, curr: T | null) => boolean
): T | User<P, U> | null {
  type Snapshot = User<P, U> | null;
  type Selection = T | null;

  const room = useRoom_withRoomContext<P, never, U, never, never, never>(
    RoomContext
  );
  const subscribe = room.events.self.subscribe;
  const getSnapshot: () => Snapshot = room.getSelf;

  const selector = maybeSelector ?? (identity as (me: User<P, U>) => T);
  const wrappedSelector = useCallback(
    (me: Snapshot): Selection => (me !== null ? selector(me) : null),
    [selector]
  );

  const getServerSnapshot = alwaysNull;

  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    getServerSnapshot,
    wrappedSelector,
    isEqual
  );
}

function useSelf<P extends JsonObject, U extends BaseUserMeta>(): User<
  P,
  U
> | null;
function useSelf<P extends JsonObject, U extends BaseUserMeta, T>(
  selector: (me: User<P, U>) => T,
  isEqual?: (prev: T | null, curr: T | null) => boolean
): T | null;
function useSelf<P extends JsonObject, U extends BaseUserMeta, T>(
  maybeSelector?: (me: User<P, U>) => T,
  isEqual?: (prev: T | null, curr: T | null) => boolean
): T | User<P, U> | null {
  return useSelf_withRoomContext<P, U, T>(
    GlobalRoomContext,
    maybeSelector as (me: User<P, U>) => T,
    isEqual
  );
}

/**
 * @internal
 */
function useMyPresence_withRoomContext<P extends JsonObject>(
  RoomContext: Context<OpaqueRoom | null>
): [P, (patch: Partial<P>, options?: { addToHistory: boolean }) => void] {
  const room = useRoom_withRoomContext<P, never, never, never, never, never>(
    RoomContext
  );
  const subscribe = room.events.myPresence.subscribe;
  const getSnapshot = room.getPresence;
  const presence = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setPresence = room.updatePresence;
  return [presence, setPresence];
}

function useMyPresence<P extends JsonObject>(): [
  P,
  (patch: Partial<P>, options?: { addToHistory: boolean }) => void,
] {
  return useMyPresence_withRoomContext<P>(GlobalRoomContext);
}

/**
 * @internal
 */
function useUpdateMyPresence_withRoomContext<P extends JsonObject>(
  RoomContext: Context<OpaqueRoom | null>
): (patch: Partial<P>, options?: { addToHistory: boolean }) => void {
  return useRoom_withRoomContext<P, never, never, never, never, never>(
    RoomContext
  ).updatePresence;
}

function useUpdateMyPresence<P extends JsonObject>(): (
  patch: Partial<P>,
  options?: { addToHistory: boolean }
) => void {
  return useUpdateMyPresence_withRoomContext<P>(GlobalRoomContext);
}

/**
 * @internal
 */
function useOthers_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
>(RoomContext: Context<OpaqueRoom | null>): readonly User<P, U>[];
function useOthers_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function useOthers_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector?: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | readonly User<P, U>[] {
  const room = useRoom_withRoomContext<P, never, U, never, never, never>(
    RoomContext
  );
  const subscribe = room.events.others.subscribe;
  const getSnapshot = room.getOthers;
  const getServerSnapshot = alwaysEmptyList;
  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    getServerSnapshot,
    selector ?? (identity as (others: readonly User<P, U>[]) => T),
    isEqual
  );
}

function useOthers<
  P extends JsonObject,
  U extends BaseUserMeta,
>(): readonly User<P, U>[];
function useOthers<P extends JsonObject, U extends BaseUserMeta, T>(
  selector: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function useOthers<P extends JsonObject, U extends BaseUserMeta, T>(
  selector?: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | readonly User<P, U>[] {
  return useOthers_withRoomContext<P, U, T>(
    GlobalRoomContext,
    selector as (others: readonly User<P, U>[]) => T,
    isEqual
  );
}

/**
 * @internal
 */
function useOthersMapped_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  itemSelector: (other: User<P, U>) => T,
  itemIsEqual?: (prev: T, curr: T) => boolean
): ReadonlyArray<readonly [connectionId: number, data: T]> {
  const wrappedSelector = useCallback(
    (others: readonly User<P, U>[]) =>
      others.map((other) => [other.connectionId, itemSelector(other)] as const),
    [itemSelector]
  );

  const wrappedIsEqual = useCallback(
    (
      a: ReadonlyArray<readonly [connectionId: number, data: T]>,
      b: ReadonlyArray<readonly [connectionId: number, data: T]>
    ): boolean => {
      const eq = itemIsEqual ?? Object.is;
      return (
        a.length === b.length &&
        a.every((atuple, index) => {
          // We know btuple always exist because we checked the array length on the previous line
          const btuple = b[index]!;
          return atuple[0] === btuple[0] && eq(atuple[1], btuple[1]);
        })
      );
    },
    [itemIsEqual]
  );

  return useOthers_withRoomContext<P, U, ReadonlyArray<readonly [number, T]>>(
    RoomContext,
    wrappedSelector,
    wrappedIsEqual
  );
}

function useOthersMapped<P extends JsonObject, U extends BaseUserMeta, T>(
  itemSelector: (other: User<P, U>) => T,
  itemIsEqual?: (prev: T, curr: T) => boolean
): ReadonlyArray<readonly [connectionId: number, data: T]> {
  return useOthersMapped_withRoomContext<P, U, T>(
    GlobalRoomContext,
    itemSelector,
    itemIsEqual
  );
}

/**
 * @internal
 */
function useOthersConnectionIds_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): readonly number[] {
  return useOthers_withRoomContext(
    RoomContext,
    selectorFor_useOthersConnectionIds,
    shallow
  );
}

/**
 * Returns an array of connection IDs. This matches the values you'll get by
 * using the `useOthers()` hook.
 *
 * Roughly equivalent to:
 *   useOthers((others) => others.map(other => other.connectionId), shallow)
 *
 * This is useful in particular to implement efficiently rendering components
 * for each user in the room, e.g. cursors.
 *
 * @example
 * const ids = useOthersConnectionIds();
 * // [2, 4, 7]
 */
function useOthersConnectionIds(): readonly number[] {
  return useOthersConnectionIds_withRoomContext(GlobalRoomContext);
}

const NOT_FOUND = Symbol();

type NotFound = typeof NOT_FOUND;

/**
 * @internal
 */
function useOther_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  connectionId: number,
  selector: (other: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T {
  const wrappedSelector = useCallback(
    (others: readonly User<P, U>[]) => {
      // TODO: Make this O(1) instead of O(n)?
      const other = others.find((other) => other.connectionId === connectionId);
      return other !== undefined ? selector(other) : NOT_FOUND;
    },
    [connectionId, selector]
  );

  const wrappedIsEqual = useCallback(
    (prev: T | NotFound, curr: T | NotFound): boolean => {
      if (prev === NOT_FOUND || curr === NOT_FOUND) {
        return prev === curr;
      }

      const eq = isEqual ?? Object.is;
      return eq(prev, curr);
    },
    [isEqual]
  );

  const other = useOthers_withRoomContext<P, U, T | NotFound>(
    RoomContext,
    wrappedSelector,
    wrappedIsEqual
  );
  if (other === NOT_FOUND) {
    throw new Error(
      `No such other user with connection id ${connectionId} exists`
    );
  }

  return other;
}

function useOther<P extends JsonObject, U extends BaseUserMeta, T>(
  connectionId: number,
  selector: (other: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T {
  return useOther_withRoomContext<P, U, T>(
    GlobalRoomContext,
    connectionId,
    selector,
    isEqual
  );
}

/**
 * @internal
 */
function useMutableStorageRoot_withRoomContext<S extends LsonObject>(
  RoomContext: Context<OpaqueRoom | null>
): LiveObject<S> | null {
  const room = useRoom_withRoomContext<never, S, never, never, never, never>(
    RoomContext
  );
  const subscribe = room.events.storageDidLoad.subscribeOnce;
  const getSnapshot = room.getStorageOrNull;
  const getServerSnapshot = alwaysNull;
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * @internal
 */
function useStorageRoot_withRoomContext<S extends LsonObject>(
  RoomContext: Context<OpaqueRoom | null>
): [root: LiveObject<S> | null] {
  return [useMutableStorageRoot_withRoomContext<S>(RoomContext)];
}

// NOTE: This API exists for backward compatible reasons
function useStorageRoot<S extends LsonObject>(): [root: LiveObject<S> | null] {
  return useStorageRoot_withRoomContext<S>(GlobalRoomContext);
}

/**
 * @internal
 */
function useStorage_withRoomContext<S extends LsonObject, T>(
  RoomContext: Context<OpaqueRoom | null>,
  selector: (root: ToJson<S>) => T,
  isEqual?: (prev: T | null, curr: T | null) => boolean
): T | null {
  type Snapshot = ToJson<S> | null;
  type Selection = T | null;

  const room = useRoom_withRoomContext<never, S, never, never, never, never>(
    RoomContext
  );
  const rootOrNull = useMutableStorageRoot_withRoomContext<S>(RoomContext);

  const wrappedSelector = useCallback(
    (rootOrNull: Snapshot): Selection =>
      rootOrNull !== null ? selector(rootOrNull) : null,
    [selector]
  );

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      rootOrNull !== null
        ? room.subscribe(rootOrNull, onStoreChange, { isDeep: true })
        : noop,
    [room, rootOrNull]
  );

  const getSnapshot = useCallback((): Snapshot => {
    if (rootOrNull === null) {
      return null;
    } else {
      const root = rootOrNull;
      return root.toJSON();
    }
  }, [rootOrNull]);

  const getServerSnapshot = alwaysNull;

  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    getServerSnapshot,
    wrappedSelector,
    isEqual
  );
}

function useStorage<S extends LsonObject, T>(
  selector: (root: ToJson<S>) => T,
  isEqual?: (prev: T | null, curr: T | null) => boolean
): T | null {
  return useStorage_withRoomContext<S, T>(GlobalRoomContext, selector, isEqual);
}

/**
 * @internal
 */
function useMutation_withRoomContext<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
  F extends (context: MutationContext<P, S, U>, ...args: any[]) => any,
>(
  RoomContext: Context<OpaqueRoom | null>,
  callback: F,
  deps: readonly unknown[]
): OmitFirstArg<F> {
  const room = useRoom_withRoomContext<P, S, U, E, TM, CM>(RoomContext);
  return useMemo(
    () => {
      return ((...args) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        room.batch(() =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          callback(
            makeMutationContext<P, S, U, E, TM, CM>(room),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            ...args
          )
        )) as OmitFirstArg<F>;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [room, ...deps]
  );
}

function useMutation<
  P extends JsonObject,
  S extends LsonObject,
  U extends BaseUserMeta,
  E extends Json,
  TM extends BaseMetadata,
  CM extends BaseMetadata,
  F extends (context: MutationContext<P, S, U>, ...args: any[]) => any,
>(callback: F, deps: readonly unknown[]): OmitFirstArg<F> {
  return useMutation_withRoomContext<P, S, U, E, TM, CM, F>(
    GlobalRoomContext,
    callback,
    deps
  );
}

/**
 * @internal
 */
function useThreads_withRoomContext<
  TM extends BaseMetadata,
  CM extends BaseMetadata,
>(
  RoomContext: Context<OpaqueRoom | null>,
  options: UseThreadsOptions<TM> = {}
): ThreadsAsyncResult<TM, CM> {
  const { scrollOnLoad = true } = options;

  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);
  const { store, getOrCreateThreadsPollerForRoomId } = getRoomExtrasForClient<
    TM,
    CM
  >(client);
  const queryKey = makeRoomThreadsQueryKey(room.id, options.query);

  const poller = getOrCreateThreadsPollerForRoomId(room.id);

  useEffect(
    () =>
      void store.outputs.loadingRoomThreads
        .getOrCreate(queryKey)
        .waitUntilLoaded()

    // NOTE: Deliberately *not* using a dependency array here!
    //
    // It is important to call waitUntil on *every* render.
    // This is harmless though, on most renders, except:
    // 1. The very first render, in which case we'll want to trigger the initial page fetch.
    // 2. All other subsequent renders now "just" return the same promise (a quick operation).
    // 3. If ever the promise would fail, then after 5 seconds it would reset, and on the very
    //    *next* render after that, a *new* fetch/promise will get created.
  );

  useEffect(() => {
    poller.inc();
    poller.pollNowIfStale();
    return () => poller.dec();
  }, [poller]);

  const result = useSignal(
    store.outputs.loadingRoomThreads.getOrCreate(queryKey).signal
  );

  useScrollToCommentOnLoadEffect(scrollOnLoad, result);
  return result;
}

function useFeeds_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  options?: UseFeedsOptions
): FeedsAsyncResult {
  const room = useRoom_withRoomContext(RoomContext);
  const client = useClient();
  const { store } = getRoomExtrasForClient(client);
  const queryKey = makeFeedsQueryKey(room.id, options);

  const loadableResource = store.outputs.loadingFeeds.getOrCreate(queryKey);

  useEffect(() => {
    void loadableResource.waitUntilLoaded();
  }, [room, loadableResource]);

  return useSignal(loadableResource.signal);
}

function useFeeds(options?: UseFeedsOptions): FeedsAsyncResult {
  return useFeeds_withRoomContext(GlobalRoomContext, options);
}

function useFeedMessages_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  feedId: string,
  options?: UseFeedMessagesOptions
): FeedMessagesAsyncResult {
  const room = useRoom_withRoomContext(RoomContext);
  const client = useClient();
  const { store } = getRoomExtrasForClient(client);
  const queryKey = makeFeedMessagesQueryKey(room.id, feedId, options);

  useEffect(() => {
    void store.outputs.loadingFeedMessages
      .getOrCreate(queryKey)
      .waitUntilLoaded();
  });

  return useSignal(
    store.outputs.loadingFeedMessages.getOrCreate(queryKey).signal
  );
}

function useFeedMessages(
  feedId: string,
  options?: UseFeedMessagesOptions
): FeedMessagesAsyncResult {
  return useFeedMessages_withRoomContext(GlobalRoomContext, feedId, options);
}

function useFeedsSuspense_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  options?: UseFeedsOptions
): FeedsAsyncSuccess {
  ensureNotServerSide();
  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);

  const { store } = getRoomExtrasForClient(client);
  const queryKey = makeFeedsQueryKey(room.id, options);

  use(store.outputs.loadingFeeds.getOrCreate(queryKey).waitUntilLoaded());

  const result = useFeeds_withRoomContext(RoomContext, options);
  assert(!result.error, "Did not expect error");
  assert(!result.isLoading, "Did not expect loading");
  return result as FeedsAsyncSuccess;
}

function useFeedsSuspense(options?: UseFeedsOptions): FeedsAsyncSuccess {
  return useFeedsSuspense_withRoomContext(GlobalRoomContext, options);
}

function useFeedMessagesSuspense_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  feedId: string,
  options?: UseFeedMessagesOptions
): FeedMessagesAsyncSuccess {
  ensureNotServerSide();

  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);

  const { store } = getRoomExtrasForClient(client);
  const queryKey = makeFeedMessagesQueryKey(room.id, feedId, options);

  use(
    store.outputs.loadingFeedMessages.getOrCreate(queryKey).waitUntilLoaded()
  );

  const result = useFeedMessages_withRoomContext(RoomContext, feedId, options);
  assert(!result.error, "Did not expect error");
  assert(!result.isLoading, "Did not expect loading");
  return result as FeedMessagesAsyncSuccess;
}

function useFeedMessagesSuspense(
  feedId: string,
  options?: UseFeedMessagesOptions
): FeedMessagesAsyncSuccess {
  return useFeedMessagesSuspense_withRoomContext(
    GlobalRoomContext,
    feedId,
    options
  );
}

function useCreateFeed_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (
  feedId: string,
  options?: { metadata?: FeedCreateMetadata; createdAt?: number }
) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (feedId, options) => room.addFeed(feedId, options),
    [room]
  );
}

/**
 * Returns a function that creates a new feed in the current room.
 *
 * @example
 * const createFeed = useCreateFeed();
 * createFeed("feed-id", { metadata: { name: "My Feed" } });
 */
function useCreateFeed(): (
  feedId: string,
  options?: { metadata?: FeedCreateMetadata; createdAt?: number }
) => Promise<void> {
  return useCreateFeed_withRoomContext(GlobalRoomContext);
}

function useDeleteFeed_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (feedId: string) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback((feedId) => room.deleteFeed(feedId), [room]);
}

/**
 * Returns a function that deletes a feed from the current room.
 *
 * @example
 * const deleteFeed = useDeleteFeed();
 * deleteFeed("feed-id");
 */
function useDeleteFeed(): (feedId: string) => Promise<void> {
  return useDeleteFeed_withRoomContext(GlobalRoomContext);
}

function useUpdateFeedMetadata_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (feedId: string, metadata: FeedUpdateMetadata) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (feedId, metadata) => room.updateFeed(feedId, metadata),
    [room]
  );
}

/**
 * Returns a function that updates a feed's metadata in the current room.
 *
 * @example
 * const updateFeedMetadata = useUpdateFeedMetadata();
 * updateFeedMetadata("feed-id", { name: "Updated Name" });
 */
function useUpdateFeedMetadata(): (
  feedId: string,
  metadata: FeedUpdateMetadata
) => Promise<void> {
  return useUpdateFeedMetadata_withRoomContext(GlobalRoomContext);
}

function useCreateFeedMessage_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (
  feedId: string,
  data: JsonObject,
  options?: { id?: string; createdAt?: number }
) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (feedId, data, options) => room.addFeedMessage(feedId, data, options),
    [room]
  );
}

/**
 * Returns a function that adds a message to a feed in the current room.
 *
 * @example
 * const createFeedMessage = useCreateFeedMessage();
 * createFeedMessage("feed-id", { text: "Hello" });
 */
function useCreateFeedMessage(): (
  feedId: string,
  data: JsonObject,
  options?: { id?: string; createdAt?: number }
) => Promise<void> {
  return useCreateFeedMessage_withRoomContext(GlobalRoomContext);
}

function useDeleteFeedMessage_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (feedId: string, messageId: string) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (feedId, messageId) => room.deleteFeedMessage(feedId, messageId),
    [room]
  );
}

/**
 * Returns a function that deletes a message from a feed in the current room.
 *
 * @example
 * const deleteFeedMessage = useDeleteFeedMessage();
 * deleteFeedMessage("feed-id", "message-id");
 */
function useDeleteFeedMessage(): (
  feedId: string,
  messageId: string
) => Promise<void> {
  return useDeleteFeedMessage_withRoomContext(GlobalRoomContext);
}

function useUpdateFeedMessage_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (
  feedId: string,
  messageId: string,
  data: JsonObject,
  options?: { updatedAt?: number }
) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (feedId, messageId, data, options) =>
      room.updateFeedMessage(feedId, messageId, data, options),
    [room]
  );
}

/**
 * Returns a function that updates a feed message in the current room.
 *
 * @example
 * const updateFeedMessage = useUpdateFeedMessage();
 * updateFeedMessage("feed-id", "message-id", { text: "Updated" });
 */
function useUpdateFeedMessage(): (
  feedId: string,
  messageId: string,
  data: JsonObject,
  options?: { updatedAt?: number }
) => Promise<void> {
  return useUpdateFeedMessage_withRoomContext(GlobalRoomContext);
}

function useThreads<TM extends BaseMetadata, CM extends BaseMetadata>(
  options: UseThreadsOptions<TM> = {}
): ThreadsAsyncResult<TM, CM> {
  return useThreads_withRoomContext<TM, CM>(GlobalRoomContext, options);
}

/**
 * @internal
 */
function useSearchComments_withRoomContext<TM extends BaseMetadata>(
  RoomContext: Context<OpaqueRoom | null>,
  options: UseSearchCommentsOptions<TM>
): SearchCommentsAsyncResult {
  const [result, setResult] = useState<SearchCommentsAsyncResult>({
    isLoading: true,
  });

  const currentRequestInfo = useRef<{
    id: number;
    controller: AbortController;
  } | null>(null);

  const timeout = useRef<number | null>(null);

  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);

  const queryKey = stableStringify([room.id, options.query]);

  useEffect(() => {
    const currentRequestId = (currentRequestInfo.current?.id ?? 0) + 1;
    const controller = new AbortController();

    currentRequestInfo.current = { id: currentRequestId, controller };
    setResult((result) => {
      if (result.isLoading) return result;
      // **NOTE**: Should we keep the old result but only set loading to true.
      // All our other hooks (useThreads) is defined in the way so that if the result is loading, the result is undefined.
      return { isLoading: true };
    });

    timeout.current = window.setTimeout(() => {
      client[kInternal].httpClient
        .searchComments(
          {
            roomId: room.id,
            query: options.query,
          },
          { signal: controller.signal }
        )
        .then(({ data }) => {
          // If the request was aborted, we do not update the result received from this request as it may be stale.
          if (controller.signal.aborted) return;

          // If a new request was made while this request was in flight, we do not update the result received from this request as it may be stale.
          if (currentRequestInfo.current?.id !== currentRequestId) return;

          setResult({ isLoading: false, results: data });

          // Clear the current request info to avoid stale results from the next request.
          currentRequestInfo.current = null;
        })
        .catch((err) => {
          // If the request was aborted, we do not update the result received from this request as it may be stale.
          if (controller.signal.aborted) return;

          // If a new request was made while this request was in flight, we do not update the result received from this request as it may be stale.
          if (currentRequestInfo.current?.id !== currentRequestId) return;

          setResult({ isLoading: false, error: err as Error });

          // Clear the current request info to avoid stale results from the next request.
          currentRequestInfo.current = null;
        });
    }, 300 /* debounce time */);

    return () => {
      // If there is a timeout in progress, cancel it before we initiate a new one
      if (timeout.current !== null) {
        window.clearTimeout(timeout.current);
      }

      // Cancel any in-flight request and initiate a new request
      if (currentRequestInfo.current !== null) {
        currentRequestInfo.current.controller.abort();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, client, room.id]);

  return result;
}

function useSearchComments<TM extends BaseMetadata>(
  options: UseSearchCommentsOptions<TM>
): SearchCommentsAsyncResult {
  return useSearchComments_withRoomContext<TM>(GlobalRoomContext, options);
}

/**
 * @internal
 */
function useCreateThread_withRoomContext<
  TM extends BaseMetadata,
  CM extends BaseMetadata,
>(
  RoomContext: Context<OpaqueRoom | null>
): (options: CreateThreadOptions<TM, CM>) => ThreadData<TM, CM> {
  return useCreateRoomThread(useRoom_withRoomContext(RoomContext).id);
}

function useCreateThread<TM extends BaseMetadata, CM extends BaseMetadata>(): (
  options: CreateThreadOptions<TM, CM>
) => ThreadData<TM, CM> {
  return useCreateThread_withRoomContext<TM, CM>(GlobalRoomContext);
}

/**
 * @private
 */
function useCreateRoomThread<TM extends BaseMetadata, CM extends BaseMetadata>(
  roomId: string
): (options: CreateThreadOptions<TM, CM>) => ThreadData<TM, CM> {
  const client = useClient();

  return useCallback(
    (options: CreateThreadOptions<TM, CM>): ThreadData<TM, CM> => {
      const body = options.body;
      const metadata = options.metadata ?? ({} as TM);
      const commentMetadata = options.commentMetadata ?? ({} as CM);
      const attachments = options.attachments;
      const visibility = options.visibility ?? "public";

      const threadId = createThreadId();
      const commentId = createCommentId();
      const createdAt = new Date();

      const newComment: CommentData<CM> = {
        id: commentId,
        threadId,
        roomId,
        createdAt,
        type: "comment",
        userId: getCurrentUserId(client),
        body,
        reactions: [],
        attachments: attachments ?? [],
        metadata: commentMetadata,
      };
      const newThread: ThreadData<TM, CM> = {
        id: threadId,
        type: "thread",
        createdAt,
        updatedAt: createdAt,
        roomId,
        metadata,
        comments: [newComment],
        resolved: false,
        visibility,
      };

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "create-thread",
        thread: newThread,
        roomId,
      });

      const attachmentIds = attachments?.map((attachment) => attachment.id);

      client[kInternal].httpClient
        .createThread({
          roomId,
          threadId,
          commentId,
          body,
          visibility,
          metadata,
          commentMetadata,
          attachmentIds,
        })
        .then(
          (thread) => {
            // Replace the optimistic update by the real thing
            store.createThread(optimisticId, thread);
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "CREATE_THREAD_ERROR",
                roomId,
                threadId,
                commentId,
                body,
                visibility,
                metadata,
                commentMetadata,
              },
              err
            )
        );

      return newThread;
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useDeleteThread_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (threadId: string) => void {
  return useDeleteRoomThread(useRoom_withRoomContext(RoomContext).id);
}

function useDeleteThread(): (threadId: string) => void {
  return useDeleteThread_withRoomContext(GlobalRoomContext);
}

function useDeleteRoomThread(roomId: string): (threadId: string) => void {
  const client = useClient();
  return useCallback(
    (threadId: string): void => {
      const { store, onMutationFailure } = getRoomExtrasForClient(client);

      const userId = getCurrentUserId(client);

      const existing = store.outputs.threads.get().get(threadId);
      if (existing?.comments?.[0]?.userId !== userId) {
        throw new Error("Only the thread creator can delete the thread");
      }

      const optimisticId = store.optimisticUpdates.add({
        type: "delete-thread",
        roomId,
        threadId,
        deletedAt: new Date(),
      });

      client[kInternal].httpClient
        .deleteThread({
          roomId,
          threadId,
          visibility: existing.visibility,
        })
        .then(
          () => {
            // Replace the optimistic update by the real thing
            store.deleteThread(threadId, optimisticId);
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              { type: "DELETE_THREAD_ERROR", roomId, threadId },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useEditThreadMetadata_withRoomContext<TM extends BaseMetadata>(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useEditRoomThreadMetadata<TM>(useRoom_withRoomContext(RoomContext).id);
}

function useEditThreadMetadata<TM extends BaseMetadata>() {
  return useEditThreadMetadata_withRoomContext<TM>(GlobalRoomContext);
}

function useEditRoomThreadMetadata<TM extends BaseMetadata>(roomId: string) {
  const client = useClient();
  return useCallback(
    (options: EditThreadMetadataOptions<TM>): void => {
      if (!options.metadata) {
        return;
      }

      const threadId = options.threadId;
      const metadata = options.metadata;
      const updatedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "edit-thread-metadata",
        metadata,
        threadId,
        updatedAt,
      });

      client[kInternal].httpClient
        .editThreadMetadata({
          roomId,
          threadId,
          metadata,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          (metadata) =>
            // Replace the optimistic update by the real thing
            store.patchThread(threadId, optimisticId, { metadata }, updatedAt),
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "EDIT_THREAD_METADATA_ERROR",
                roomId,
                threadId,
                metadata,
              },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useEditCommentMetadata_withRoomContext<CM extends BaseMetadata>(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useEditRoomCommentMetadata<CM>(
    useRoom_withRoomContext(RoomContext).id
  );
}

function useEditCommentMetadata<CM extends BaseMetadata>() {
  return useEditCommentMetadata_withRoomContext<CM>(GlobalRoomContext);
}

function useEditRoomCommentMetadata<CM extends BaseMetadata>(roomId: string) {
  const client = useClient();
  return useCallback(
    (options: EditCommentMetadataOptions<CM>): void => {
      if (!options.metadata) {
        return;
      }

      const threadId = options.threadId;
      const commentId = options.commentId;
      const metadata = options.metadata;
      const updatedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "edit-comment-metadata",
        threadId,
        commentId,
        metadata,
        updatedAt,
      });

      client[kInternal].httpClient
        .editCommentMetadata({
          roomId,
          threadId,
          commentId,
          metadata,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          (updatedMetadata) =>
            // Replace the optimistic update by the real thing
            store.editCommentMetadata(
              threadId,
              commentId,
              optimisticId,
              updatedMetadata,
              updatedAt
            ),
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "EDIT_COMMENT_METADATA_ERROR",
                roomId,
                threadId,
                commentId,
                metadata,
              },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useCreateComment_withRoomContext<CM extends BaseMetadata>(
  RoomContext: Context<OpaqueRoom | null>
): (options: CreateCommentOptions<CM>) => CommentData<CM> {
  return useCreateRoomComment(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that adds a comment to a thread.
 *
 * @example
 * const createComment = useCreateComment();
 * createComment({ threadId: "th_xxx", body: {} });
 */
function useCreateComment<CM extends BaseMetadata>(): (
  options: CreateCommentOptions<CM>
) => CommentData<CM> {
  return useCreateComment_withRoomContext<CM>(GlobalRoomContext);
}

/**
 * @private
 */
function useCreateRoomComment<CM extends BaseMetadata>(
  roomId: string
): (options: CreateCommentOptions<CM>) => CommentData<CM> {
  const client = useClient();
  return useCallback(
    (options: CreateCommentOptions<CM>): CommentData<CM> => {
      const { threadId, body } = options;
      const metadata = options.metadata ?? ({} as CM);
      const attachments = options.attachments ?? [];
      const commentId = createCommentId();
      const createdAt = new Date();

      const comment: CommentData<CM> = {
        id: commentId,
        threadId,
        roomId,
        type: "comment",
        createdAt,
        userId: getCurrentUserId(client),
        body,
        reactions: [],
        attachments: attachments ?? [],
        metadata,
      };

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "create-comment",
        comment,
      });

      const attachmentIds = attachments?.map((attachment) => attachment.id);

      client[kInternal].httpClient
        .createComment({
          roomId,
          threadId,
          commentId,
          body,
          metadata,
          attachmentIds,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          (newComment) => {
            // Replace the optimistic update by the real thing
            store.createComment(newComment, optimisticId);
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "CREATE_COMMENT_ERROR",
                roomId,
                threadId,
                commentId,
                body,
                metadata,
              },
              err
            )
        );

      return comment;
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useEditComment_withRoomContext<CM extends BaseMetadata>(
  RoomContext: Context<OpaqueRoom | null>
): (options: EditCommentOptions<CM>) => void {
  return useEditRoomComment<CM>(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that edits a comment.
 *
 * @example
 * const editComment = useEditComment()
 * editComment({ threadId: "th_xxx", commentId: "cm_xxx", body: {} })
 */
function useEditComment<CM extends BaseMetadata>(): (
  options: EditCommentOptions<CM>
) => void {
  return useEditComment_withRoomContext<CM>(GlobalRoomContext);
}

/**
 * @private
 */
function useEditRoomComment<CM extends BaseMetadata>(
  roomId: string
): (options: EditCommentOptions<CM>) => void {
  const client = useClient();
  return useCallback(
    ({
      threadId,
      commentId,
      body,
      attachments,
      metadata,
    }: EditCommentOptions<CM>): void => {
      const editedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const existing = store.outputs.threads.get().getEvenIfDeleted(threadId);

      if (existing === undefined) {
        console.warn(
          `Internal unexpected behavior. Cannot edit comment in thread "${threadId}" because the thread does not exist in the cache.`
        );
        return;
      }

      const comment = existing.comments.find(
        (comment) => comment.id === commentId
      );

      if (comment === undefined || comment.deletedAt !== undefined) {
        console.warn(
          `Internal unexpected behavior. Cannot edit comment "${commentId}" in thread "${threadId}" because the comment does not exist in the cache.`
        );
        return;
      }

      const updatedMetadata =
        metadata !== undefined
          ? {
              ...comment.metadata,
              ...metadata,
            }
          : comment.metadata;

      const optimisticId = store.optimisticUpdates.add({
        type: "edit-comment",
        comment: {
          ...comment,
          editedAt,
          body,
          attachments: attachments ?? [],
          metadata: updatedMetadata,
        },
      });

      const attachmentIds = attachments?.map((attachment) => attachment.id);

      client[kInternal].httpClient
        .editComment({
          roomId,
          threadId,
          commentId,
          body,
          attachmentIds,
          metadata,
          visibility: existing.visibility,
        })
        .then(
          (editedComment) => {
            // Replace the optimistic update by the real thing
            store.editComment(threadId, optimisticId, editedComment);
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "EDIT_COMMENT_ERROR",
                roomId,
                threadId,
                commentId,
                body,
                metadata: updatedMetadata,
              },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useDeleteComment_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useDeleteRoomComment(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that deletes a comment.
 * If it is the last non-deleted comment, the thread also gets deleted.
 *
 * @example
 * const deleteComment = useDeleteComment();
 * deleteComment({ threadId: "th_xxx", commentId: "cm_xxx" })
 */
function useDeleteComment() {
  return useDeleteComment_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useDeleteRoomComment(roomId: string) {
  const client = useClient();

  return useCallback(
    ({ threadId, commentId }: DeleteCommentOptions): void => {
      const deletedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);

      const optimisticId = store.optimisticUpdates.add({
        type: "delete-comment",
        threadId,
        commentId,
        deletedAt,
        roomId,
      });

      client[kInternal].httpClient
        .deleteComment({
          roomId,
          threadId,
          commentId,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          () => {
            // Replace the optimistic update by the real thing
            store.deleteComment(threadId, optimisticId, commentId, deletedAt);
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              { type: "DELETE_COMMENT_ERROR", roomId, threadId, commentId },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useAddReaction_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useAddRoomCommentReaction(useRoom_withRoomContext(RoomContext).id);
}

function useAddReaction() {
  return useAddReaction_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useAddRoomCommentReaction(roomId: string) {
  const client = useClient();
  return useCallback(
    ({ threadId, commentId, emoji }: CommentReactionOptions): void => {
      const createdAt = new Date();
      const userId = getCurrentUserId(client);

      const { store, onMutationFailure } = getRoomExtrasForClient(client);

      const optimisticId = store.optimisticUpdates.add({
        type: "add-reaction",
        threadId,
        commentId,
        reaction: {
          emoji,
          userId,
          createdAt,
        },
      });

      client[kInternal].httpClient
        .addReaction({
          roomId,
          threadId,
          commentId,
          emoji,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          (addedReaction) => {
            // Replace the optimistic update by the real thing
            store.addReaction(
              threadId,
              optimisticId,
              commentId,
              addedReaction,
              createdAt
            );
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "ADD_REACTION_ERROR",
                roomId,
                threadId,
                commentId,
                emoji,
              },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useRemoveReaction_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useRemoveRoomCommentReaction(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that removes a reaction on a comment.
 *
 * @example
 * const removeReaction = useRemoveReaction();
 * removeReaction({ threadId: "th_xxx", commentId: "cm_xxx", emoji: "👍" })
 */
function useRemoveReaction() {
  return useRemoveReaction_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useRemoveRoomCommentReaction(roomId: string) {
  const client = useClient();
  return useCallback(
    ({ threadId, commentId, emoji }: CommentReactionOptions): void => {
      const userId = getCurrentUserId(client);

      const removedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "remove-reaction",
        threadId,
        commentId,
        emoji,
        userId,
        removedAt,
      });

      client[kInternal].httpClient
        .removeReaction({
          roomId,
          threadId,
          commentId,
          emoji,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          () => {
            // Replace the optimistic update by the real thing
            store.removeReaction(
              threadId,
              optimisticId,
              commentId,
              emoji,
              userId,
              removedAt
            );
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              {
                type: "REMOVE_REACTION_ERROR",
                roomId,
                threadId,
                commentId,
                emoji,
              },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @private
 */
function useMarkRoomThreadAsRead(roomId: string) {
  const client = useClient();
  return useCallback(
    (threadId: string) => {
      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const inboxNotification = Object.values(
        store.outputs.notifications.get().notificationsById
      ).find(
        (inboxNotification) =>
          inboxNotification.kind === "thread" &&
          inboxNotification.threadId === threadId
      );

      if (!inboxNotification) return;

      const now = new Date();

      const optimisticId = store.optimisticUpdates.add({
        type: "mark-inbox-notification-as-read",
        inboxNotificationId: inboxNotification.id,
        readAt: now,
      });

      client[kInternal].httpClient
        .markRoomInboxNotificationAsRead({
          roomId,
          inboxNotificationId: inboxNotification.id,
        })
        .then(
          () => {
            // Replace the optimistic update by the real thing
            store.markInboxNotificationRead(
              inboxNotification.id,
              now,
              optimisticId
            );
          },
          (err: Error) => {
            onMutationFailure(
              optimisticId,
              {
                type: "MARK_INBOX_NOTIFICATION_AS_READ_ERROR",
                roomId,
                inboxNotificationId: inboxNotification.id,
              },
              err
            );
            return;
          }
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useMarkThreadAsRead_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  const room = useRoom_withRoomContext(RoomContext);
  return useMarkRoomThreadAsRead(room.id);
}

/**
 * Returns a function that marks a thread as read.
 *
 * @example
 * const markThreadAsRead = useMarkThreadAsRead();
 * markThreadAsRead("th_xxx");
 */
function useMarkThreadAsRead() {
  return useMarkThreadAsRead_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useMarkThreadAsResolved_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useMarkRoomThreadAsResolved(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that marks a thread as resolved.
 *
 * @example
 * const markThreadAsResolved = useMarkThreadAsResolved();
 * markThreadAsResolved("th_xxx");
 */
function useMarkThreadAsResolved() {
  return useMarkThreadAsResolved_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useMarkRoomThreadAsResolved(roomId: string) {
  const client = useClient();
  return useCallback(
    (threadId: string) => {
      const updatedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "mark-thread-as-resolved",
        threadId,
        updatedAt,
      });

      client[kInternal].httpClient
        .markThreadAsResolved({
          roomId,
          threadId,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          () => {
            // Replace the optimistic update by the real thing
            store.patchThread(
              threadId,
              optimisticId,
              { resolved: true },
              updatedAt
            );
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              { type: "MARK_THREAD_AS_RESOLVED_ERROR", roomId, threadId },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useMarkThreadAsUnresolved_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useMarkRoomThreadAsUnresolved(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that marks a thread as unresolved.
 *
 * @example
 * const markThreadAsUnresolved = useMarkThreadAsUnresolved();
 * markThreadAsUnresolved("th_xxx");
 */
function useMarkThreadAsUnresolved() {
  return useMarkThreadAsUnresolved_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useMarkRoomThreadAsUnresolved(roomId: string) {
  const client = useClient();
  return useCallback(
    (threadId: string) => {
      const updatedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "mark-thread-as-unresolved",
        threadId,
        updatedAt,
      });

      client[kInternal].httpClient
        .markThreadAsUnresolved({
          roomId,
          threadId,
          visibility: getThreadVisibility(store, threadId),
        })
        .then(
          () => {
            // Replace the optimistic update by the real thing
            store.patchThread(
              threadId,
              optimisticId,
              { resolved: false },
              updatedAt
            );
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              { type: "MARK_THREAD_AS_UNRESOLVED_ERROR", roomId, threadId },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useSubscribeToThread_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useSubscribeToRoomThread(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that subscribes the user to a thread.
 *
 * @example
 * const subscribeToThread = useSubscribeToThread();
 * subscribeToThread("th_xxx");
 */
function useSubscribeToThread() {
  return useSubscribeToThread_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useSubscribeToRoomThread(roomId: string) {
  const client = useClient();

  return useCallback(
    (threadId: string) => {
      const subscribedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "subscribe-to-thread",
        threadId,
        subscribedAt,
      });

      client[kInternal].httpClient.subscribeToThread({ roomId, threadId }).then(
        (subscription) => {
          store.createSubscription(subscription, optimisticId);
        },
        (err: Error) =>
          onMutationFailure(
            optimisticId,
            { type: "SUBSCRIBE_TO_THREAD_ERROR", roomId, threadId },
            err
          )
      );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useUnsubscribeFromThread_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  return useUnsubscribeFromRoomThread(useRoom_withRoomContext(RoomContext).id);
}

/**
 * Returns a function that unsubscribes the user from a thread.
 *
 * @example
 * const unsubscribeFromThread = useUnsubscribeFromThread();
 * unsubscribeFromThread("th_xxx");
 */
function useUnsubscribeFromThread() {
  return useUnsubscribeFromThread_withRoomContext(GlobalRoomContext);
}

/**
 * @private
 */
function useUnsubscribeFromRoomThread(roomId: string) {
  const client = useClient();

  return useCallback(
    (threadId: string) => {
      const unsubscribedAt = new Date();

      const { store, onMutationFailure } = getRoomExtrasForClient(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "unsubscribe-from-thread",
        threadId,
        unsubscribedAt,
      });

      client[kInternal].httpClient
        .unsubscribeFromThread({ roomId, threadId })
        .then(
          () => {
            store.deleteSubscription(
              getSubscriptionKey("thread", threadId),
              optimisticId
            );
          },
          (err: Error) =>
            onMutationFailure(
              optimisticId,
              { type: "UNSUBSCRIBE_FROM_THREAD_ERROR", roomId, threadId },
              err
            )
        );
    },
    [client, roomId]
  );
}

/**
 * @internal
 */
function useThreadSubscription_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  threadId: string
): ThreadSubscription {
  return useRoomThreadSubscription(
    useRoom_withRoomContext(RoomContext).id,
    threadId
  );
}

/**
 * Returns the subscription status of a thread, methods to update it, and when
 * the thread was last read.
 *
 * @example
 * const { status, subscribe, unsubscribe, unreadSince } = useThreadSubscription("th_xxx");
 */
function useThreadSubscription(threadId: string): ThreadSubscription {
  return useThreadSubscription_withRoomContext(GlobalRoomContext, threadId);
}

/**
 * @private
 */
function useRoomThreadSubscription(
  roomId: string,
  threadId: string
): ThreadSubscription {
  const client = useClient();
  const { store } = getRoomExtrasForClient(client);
  const subscriptionKey = useMemo(
    () => getSubscriptionKey("thread", threadId),
    [threadId]
  );
  const subscribeToThread = useSubscribeToRoomThread(roomId);
  const unsubscribeFromThread = useUnsubscribeFromRoomThread(roomId);
  const subscribe = useCallback(
    () => subscribeToThread(threadId),
    [subscribeToThread, threadId]
  );
  const unsubscribe = useCallback(
    () => unsubscribeFromThread(threadId),
    [unsubscribeFromThread, threadId]
  );

  const signal = store.outputs.threadSubscriptions;

  const selector = useCallback(
    (state: SignalType<typeof signal>): ThreadSubscription => {
      const subscription = state.subscriptions[subscriptionKey];
      const notification = state.notifications.find(
        (inboxNotification) =>
          inboxNotification.kind === "thread" &&
          inboxNotification.threadId === threadId
      );

      if (subscription === undefined) {
        return { status: "not-subscribed", subscribe, unsubscribe };
      }

      return {
        status: "subscribed",
        unreadSince: notification?.readAt ?? null,
        subscribe,
        unsubscribe,
      };
    },
    [subscriptionKey, threadId, subscribe, unsubscribe]
  );

  return useSignal(signal, selector, shallow);
}

/**
 * @internal
 */
function useRoomSubscriptionSettings_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): [
  RoomSubscriptionSettingsAsyncResult,
  (settings: Partial<RoomSubscriptionSettings>) => void,
] {
  const updateRoomSubscriptionSettings =
    useUpdateRoomSubscriptionSettings_withRoomContext(RoomContext);
  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);
  const { store, getOrCreateSubscriptionSettingsPollerForRoomId } =
    getRoomExtrasForClient(client);

  const poller = getOrCreateSubscriptionSettingsPollerForRoomId(room.id);

  useEffect(
    () =>
      void store.outputs.roomSubscriptionSettingsByRoomId
        .getOrCreate(room.id)
        .waitUntilLoaded()

    // NOTE: Deliberately *not* using a dependency array here!
    //
    // It is important to call waitUntil on *every* render.
    // This is harmless though, on most renders, except:
    // 1. The very first render, in which case we'll want to trigger the initial page fetch.
    // 2. All other subsequent renders now "just" return the same promise (a quick operation).
    // 3. If ever the promise would fail, then after 5 seconds it would reset, and on the very
    //    *next* render after that, a *new* fetch/promise will get created.
  );

  useEffect(() => {
    poller.inc();
    poller.pollNowIfStale();
    return () => {
      poller.dec();
    };
  }, [poller]);

  const settings = useSignal(
    store.outputs.roomSubscriptionSettingsByRoomId.getOrCreate(room.id).signal
  );

  return useMemo(() => {
    return [settings, updateRoomSubscriptionSettings];
  }, [settings, updateRoomSubscriptionSettings]);
}

/**
 * Returns the user's subscription settings for the current room
 * and a function to update them.
 *
 * @example
 * const [{ settings }, updateSettings] = useRoomSubscriptionSettings();
 */
function useRoomSubscriptionSettings(): [
  RoomSubscriptionSettingsAsyncResult,
  (settings: Partial<RoomSubscriptionSettings>) => void,
] {
  return useRoomSubscriptionSettings_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useRoomSubscriptionSettingsSuspense_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): [
  RoomSubscriptionSettingsAsyncSuccess,
  (settings: Partial<RoomSubscriptionSettings>) => void,
] {
  // Throw error if we're calling this hook server side
  ensureNotServerSide();

  const client = useClient();
  const store = getRoomExtrasForClient(client).store;
  const room = useRoom_withRoomContext(RoomContext);

  // Suspend until there are at least some inbox notifications
  use(
    store.outputs.roomSubscriptionSettingsByRoomId
      .getOrCreate(room.id)
      .waitUntilLoaded()
  );

  // We're in a Suspense world here, and as such, the useRoomSubscriptionSettings()
  // hook is expected to only return success results when we're here.
  const [settings, updateRoomSubscriptionSettings] =
    useRoomSubscriptionSettings_withRoomContext(RoomContext);
  assert(!settings.error, "Did not expect error");
  assert(!settings.isLoading, "Did not expect loading");

  return useMemo(() => {
    return [settings, updateRoomSubscriptionSettings];
  }, [settings, updateRoomSubscriptionSettings]);
}

/**
 * Returns the user's subscription settings for the current room
 * and a function to update them.
 *
 * @example
 * const [{ settings }, updateSettings] = useRoomSubscriptionSettings();
 */
function useRoomSubscriptionSettingsSuspense(): [
  RoomSubscriptionSettingsAsyncSuccess,
  (settings: Partial<RoomSubscriptionSettings>) => void,
] {
  return useRoomSubscriptionSettingsSuspense_withRoomContext(GlobalRoomContext);
}

// A Storage version endpoint returns its snapshot as an NDJSON stream of
// StorageNode tuples (one JSON per line). Decodes it into the node array the
// SDK reconstructs/diffs against.
function parseStorageVersionNodes(ndjson: string): StorageNode[] {
  return ndjson
    .split("\n")
    .filter((line) => line.length > 0)
    .map((line) => JSON.parse(line) as StorageNode);
}

/**
 * @internal
 */
function useHistoryVersionStorageData_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  versionId: string
): HistoryVersionStorageDataAsyncResult {
  const [state, setState] = useState<HistoryVersionStorageDataAsyncResult>({
    isLoading: true,
  });
  const room = useRoom_withRoomContext(RoomContext);
  useEffect(() => {
    setState({ isLoading: true });
    const load = async () => {
      try {
        const response =
          await room[kInternal].fetchStorageHistoryVersion(versionId);

        // The endpoint returns the snapshot as an NDJSON node stream. Decode it
        // and rebuild a (detached, read-only) LiveObject tree -- typed as
        // `LsonObject` because a historical version may not match the current
        // Storage schema.
        const nodes = parseStorageVersionNodes(await response.text());
        const data = room[kInternal].liveObjectFromNodeStream(nodes);
        setState({ isLoading: false, data });
      } catch (error) {
        setState({
          isLoading: false,
          error:
            error instanceof Error
              ? error
              : new Error(
                  "An unknown error occurred while loading this version"
                ),
        });
      }
    };
    void load();
  }, [room, versionId]);
  return state;
}

/**
 * Returns the Storage data for a given version of the room.
 *
 * @example
 * const { data, isLoading, error } = useHistoryVersionStorageData(versionId);
 */
function useHistoryVersionStorageData(
  versionId: string
): HistoryVersionStorageDataAsyncResult {
  return useHistoryVersionStorageData_withRoomContext(
    GlobalRoomContext,
    versionId
  );
}

/**
 * @internal
 */
function useHistoryVersionYjsData_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  versionId: string
): HistoryVersionYjsDataAsyncResult {
  const [state, setState] = useState<HistoryVersionYjsDataAsyncResult>({
    isLoading: true,
  });
  const room = useRoom_withRoomContext(RoomContext);
  useEffect(() => {
    setState({ isLoading: true });
    const load = async () => {
      try {
        const response =
          await room[kInternal].fetchYjsHistoryVersion(versionId);
        const buffer = await response.arrayBuffer();
        const data = new Uint8Array(buffer);
        setState({
          isLoading: false,
          data,
        });
      } catch (error) {
        setState({
          isLoading: false,
          error:
            error instanceof Error
              ? error
              : new Error(
                  "An unknown error occurred while loading this version"
                ),
        });
      }
    };
    void load();
  }, [room, versionId]);
  return state;
}

/**
 * @deprecated Use `useHistoryVersionYjsData(versionId)` instead.
 *
 * Returns the version data binary for a given version
 *
 * @example
 * const {data} = useHistoryVersionData(versionId);
 */
function useHistoryVersionData(
  versionId: string
): HistoryVersionYjsDataAsyncResult {
  return useHistoryVersionYjsData_withRoomContext(GlobalRoomContext, versionId);
}

/**
 * Returns the Yjs data for a given version of the room.
 *
 * @example
 * const { data, isLoading, error } = useHistoryVersionYjsData(versionId);
 */
function useHistoryVersionYjsData(
  versionId: string
): HistoryVersionYjsDataAsyncResult {
  return useHistoryVersionYjsData_withRoomContext(GlobalRoomContext, versionId);
}

/**
 * @internal
 */
function useHistoryVersions_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): HistoryVersionsAsyncResult {
  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);

  const { store, getOrCreateVersionsPollerForRoomId } =
    getRoomExtrasForClient(client);

  const poller = getOrCreateVersionsPollerForRoomId(room.id);

  useEffect(() => {
    poller.inc();
    poller.pollNowIfStale();
    return () => poller.dec();
  }, [poller]);

  useEffect(
    () =>
      void store.outputs.versionsByRoomId.getOrCreate(room.id).waitUntilLoaded()

    // NOTE: Deliberately *not* using a dependency array here!
    //
    // It is important to call waitUntil on *every* render.
    // This is harmless though, on most renders, except:
    // 1. The very first render, in which case we'll want to trigger the initial page fetch.
    // 2. All other subsequent renders now "just" return the same promise (a quick operation).
    // 3. If ever the promise would fail, then after 5 seconds it would reset, and on the very
    //    *next* render after that, a *new* fetch/promise will get created.
  );

  return useSignal(store.outputs.versionsByRoomId.getOrCreate(room.id).signal);
}

/**
 * @internal
 */
function useDeleteHistoryVersion_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): (versionId: string) => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (versionId: string) => room[kInternal].deleteHistoryVersion(versionId),
    [room]
  );
}

/**
 * Returns a function that permanently deletes a version from the room's history.
 *
 * @example
 * const deleteHistoryVersion = useDeleteHistoryVersion();
 * deleteHistoryVersion(versionId);
 */
function useDeleteHistoryVersion(): (versionId: string) => Promise<void> {
  return useDeleteHistoryVersion_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useRestoreToStorageVersion_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  versionId: string
): () => Promise<void> {
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(async () => {
    const response =
      await room[kInternal].fetchStorageHistoryVersion(versionId);
    const nodes = parseStorageVersionNodes(await response.text());
    room[kInternal].reconcileStorageWithNodes(nodes);
  }, [room, versionId]);
}

/**
 * Returns a function that restores the room's Storage to the given historic
 * version, applied as a single undoable change (broadcast to other clients).
 *
 * @example
 * const restore = useRestoreToStorageVersion(versionId);
 * await restore();
 */
function useRestoreToStorageVersion(versionId: string): () => Promise<void> {
  return useRestoreToStorageVersion_withRoomContext(
    GlobalRoomContext,
    versionId
  );
}

/**
 * (Private beta) Returns a history of versions of the current room.
 *
 * @example
 * const { versions, error, isLoading } = useHistoryVersions();
 */
function useHistoryVersions(): HistoryVersionsAsyncResult {
  return useHistoryVersions_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useHistoryVersionsSuspense_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): HistoryVersionsAsyncSuccess {
  // Throw error if we're calling this hook server side
  ensureNotServerSide();

  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);
  const store = getRoomExtrasForClient(client).store;

  use(store.outputs.versionsByRoomId.getOrCreate(room.id).waitUntilLoaded());

  const result = useHistoryVersions_withRoomContext(RoomContext);
  assert(!result.error, "Did not expect error");
  assert(!result.isLoading, "Did not expect loading");
  return result;
}

/**
 * (Private beta) Returns a history of versions of the current room.
 *
 * @example
 * const { versions } = useHistoryVersions();
 */
function useHistoryVersionsSuspense(): HistoryVersionsAsyncSuccess {
  return useHistoryVersionsSuspense_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useUpdateRoomSubscriptionSettings_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
) {
  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);
  return useCallback(
    (settings: Partial<RoomSubscriptionSettings>) => {
      const { store, onMutationFailure, pollThreadsForRoomId } =
        getRoomExtrasForClient(client);
      const userId = getCurrentUserId(client);
      const optimisticId = store.optimisticUpdates.add({
        type: "update-room-subscription-settings",
        roomId: room.id,
        userId,
        settings,
      });

      room.updateSubscriptionSettings(settings).then(
        (udpatedSettings) => {
          // Replace the optimistic update by the real thing
          store.updateRoomSubscriptionSettings(
            room.id,
            optimisticId,
            udpatedSettings
          );

          // If the `threads` settings are changed, trigger a polling to update thread subscriptions
          if (settings.threads) {
            pollThreadsForRoomId(room.id);
          }
        },
        (err: Error) =>
          onMutationFailure(
            optimisticId,
            {
              type: "UPDATE_ROOM_SUBSCRIPTION_SETTINGS_ERROR",
              roomId: room.id,
            },
            err
          )
      );
    },
    [client, room]
  );
}

/**
 * Returns a function that updates the user's subscription settings
 * for the current room.
 *
 * @example
 * const updateRoomSubscriptionSettings = useUpdateRoomSubscriptionSettings();
 * updateRoomSubscriptionSettings({ threads: "all" });
 */
function useUpdateRoomSubscriptionSettings() {
  return useUpdateRoomSubscriptionSettings_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useSuspendUntilPresenceReady_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): void {
  // Throw error if we're calling this hook server side
  ensureNotServerSide();

  const room = useRoom_withRoomContext(RoomContext);
  use(room.waitUntilPresenceReady());
}

/**
 * @private For internal use only. Do not rely on this hook.
 */
export function useSuspendUntilPresenceReady(): void {
  return useSuspendUntilPresenceReady_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useSelfSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
>(RoomContext: Context<OpaqueRoom | null>): User<P, U>;
function useSelfSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector: (me: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function useSelfSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector?: (me: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | User<P, U> {
  useSuspendUntilPresenceReady_withRoomContext(RoomContext);
  return useSelf_withRoomContext<P, U, T>(
    RoomContext,
    selector as (me: User<P, U>) => T,
    isEqual as (prev: T | null, curr: T | null) => boolean
  ) as T | User<P, U>;
}

function useSelfSuspense<P extends JsonObject, U extends BaseUserMeta>(): User<
  P,
  U
>;
function useSelfSuspense<P extends JsonObject, U extends BaseUserMeta, T>(
  selector: (me: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function useSelfSuspense<P extends JsonObject, U extends BaseUserMeta, T>(
  selector?: (me: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | User<P, U> {
  return useSelfSuspense_withRoomContext<P, U, T>(
    GlobalRoomContext,
    selector as (me: User<P, U>) => T,
    isEqual
  );
}

/**
 * @internal
 */
function useOthersSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
>(RoomContext: Context<OpaqueRoom | null>): readonly User<P, U>[];
function useOthersSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function useOthersSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  selector?: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | readonly User<P, U>[] {
  useSuspendUntilPresenceReady_withRoomContext(RoomContext);
  return useOthers_withRoomContext<P, U, T>(
    RoomContext,
    selector as (others: readonly User<P, U>[]) => T,
    isEqual as (prev: T, curr: T) => boolean
  ) as T | readonly User<P, U>[];
}

function useOthersSuspense<
  P extends JsonObject,
  U extends BaseUserMeta,
>(): readonly User<P, U>[];
function useOthersSuspense<P extends JsonObject, U extends BaseUserMeta, T>(
  selector: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function useOthersSuspense<P extends JsonObject, U extends BaseUserMeta, T>(
  selector?: (others: readonly User<P, U>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | readonly User<P, U>[] {
  return useOthersSuspense_withRoomContext<P, U, T>(
    GlobalRoomContext,
    selector as (others: readonly User<P, U>[]) => T,
    isEqual
  );
}

/**
 * @internal
 */
function useOthersConnectionIdsSuspense_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): readonly number[] {
  useSuspendUntilPresenceReady_withRoomContext(RoomContext);
  return useOthersConnectionIds_withRoomContext(RoomContext);
}

/**
 * Returns an array of connection IDs. This matches the values you'll get by
 * using the `useOthers()` hook.
 *
 * Roughly equivalent to:
 *   useOthers((others) => others.map(other => other.connectionId), shallow)
 *
 * This is useful in particular to implement efficiently rendering components
 * for each user in the room, e.g. cursors.
 *
 * @example
 * const ids = useOthersConnectionIds();
 * // [2, 4, 7]
 */
function useOthersConnectionIdsSuspense(): readonly number[] {
  return useOthersConnectionIdsSuspense_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useOthersMappedSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  itemSelector: (other: User<P, U>) => T,
  itemIsEqual?: (prev: T, curr: T) => boolean
): ReadonlyArray<readonly [connectionId: number, data: T]> {
  useSuspendUntilPresenceReady_withRoomContext(RoomContext);
  return useOthersMapped_withRoomContext<P, U, T>(
    RoomContext,
    itemSelector,
    itemIsEqual
  );
}

function useOthersMappedSuspense<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  itemSelector: (other: User<P, U>) => T,
  itemIsEqual?: (prev: T, curr: T) => boolean
): ReadonlyArray<readonly [connectionId: number, data: T]> {
  return useOthersMappedSuspense_withRoomContext<P, U, T>(
    GlobalRoomContext,
    itemSelector,
    itemIsEqual
  );
}

/**
 * @internal
 */
function useOtherSuspense_withRoomContext<
  P extends JsonObject,
  U extends BaseUserMeta,
  T,
>(
  RoomContext: Context<OpaqueRoom | null>,
  connectionId: number,
  selector: (other: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T {
  useSuspendUntilPresenceReady_withRoomContext(RoomContext);
  return useOther_withRoomContext<P, U, T>(
    RoomContext,
    connectionId,
    selector,
    isEqual
  );
}

function useOtherSuspense<P extends JsonObject, U extends BaseUserMeta, T>(
  connectionId: number,
  selector: (other: User<P, U>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T {
  return useOtherSuspense_withRoomContext<P, U, T>(
    GlobalRoomContext,
    connectionId,
    selector,
    isEqual
  );
}

/**
 * @internal
 */
function useSuspendUntilStorageReady_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>
): void {
  // Throw error if we're calling this hook server side
  ensureNotServerSide();

  const room = useRoom_withRoomContext(RoomContext);
  use(room.waitUntilStorageReady());
}

/**
 * @private For internal use only. Do not rely on this hook.
 */
export function useSuspendUntilStorageReady(): void {
  return useSuspendUntilStorageReady_withRoomContext(GlobalRoomContext);
}

/**
 * @internal
 */
function useStorageSuspense_withRoomContext<S extends LsonObject, T>(
  RoomContext: Context<OpaqueRoom | null>,
  selector: (root: ToJson<S>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T {
  useSuspendUntilStorageReady_withRoomContext(RoomContext);
  return useStorage_withRoomContext(
    RoomContext,
    selector,
    isEqual as (prev: T | null, curr: T | null) => boolean
  ) as T;
}

function useStorageSuspense<S extends LsonObject, T>(
  selector: (root: ToJson<S>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T {
  return useStorageSuspense_withRoomContext<S, T>(
    GlobalRoomContext,
    selector,
    isEqual
  );
}

/**
 * @internal
 */
function useThreadsSuspense_withRoomContext<
  TM extends BaseMetadata,
  CM extends BaseMetadata,
>(
  RoomContext: Context<OpaqueRoom | null>,
  options: UseThreadsOptions<TM> = {}
): ThreadsAsyncSuccess<TM, CM> {
  // Throw error if we're calling this hook server side
  ensureNotServerSide();

  const client = useClient();
  const room = useRoom_withRoomContext(RoomContext);

  const { store } = getRoomExtrasForClient<TM, CM>(client);
  const queryKey = makeRoomThreadsQueryKey(room.id, options.query);

  use(store.outputs.loadingRoomThreads.getOrCreate(queryKey).waitUntilLoaded());

  const result = useThreads_withRoomContext<TM, CM>(RoomContext, options);
  assert(!result.error, "Did not expect error");
  assert(!result.isLoading, "Did not expect loading");
  return result;
}

function useThreadsSuspense<TM extends BaseMetadata, CM extends BaseMetadata>(
  options: UseThreadsOptions<TM> = {}
): ThreadsAsyncSuccess<TM, CM> {
  return useThreadsSuspense_withRoomContext<TM, CM>(GlobalRoomContext, options);
}

function selectorFor_useAttachmentUrl(
  state: AsyncResult<string | undefined> | undefined
): AttachmentUrlAsyncResult {
  if (state === undefined || state?.isLoading) {
    return state ?? { isLoading: true };
  }

  if (state.error) {
    return state;
  }

  // For now `useAttachmentUrl` doesn't support a custom resolver so this case
  // will never happen as `getAttachmentUrl` will either return a URL or throw.
  // But we might decide to offer a custom resolver in the future to allow
  // self-hosting attachments.
  assert(state.data !== undefined, "Unexpected missing attachment URL");

  return {
    isLoading: false,
    url: state.data,
  };
}

/**
 * @internal
 */
function useAttachmentUrl_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  attachmentId: string
): AttachmentUrlAsyncResult {
  const room = useRoom_withRoomContext(RoomContext);
  return useRoomAttachmentUrl(attachmentId, room.id);
}

/**
 * Returns a presigned URL for an attachment by its ID.
 *
 * @example
 * const { url, error, isLoading } = useAttachmentUrl("at_xxx");
 */
function useAttachmentUrl(attachmentId: string): AttachmentUrlAsyncResult {
  return useAttachmentUrl_withRoomContext(GlobalRoomContext, attachmentId);
}

/**
 * @private For internal use only. Do not rely on this hook. Use `useAttachmentUrl` instead.
 */
function useRoomAttachmentUrl(
  attachmentId: string,
  roomId: string
): AttachmentUrlAsyncResult {
  const client = useClient();
  const store =
    client[kInternal].httpClient.getOrCreateAttachmentUrlsStore(roomId);

  const getAttachmentUrlState = useCallback(
    () => store.getItemState(attachmentId),
    [store, attachmentId]
  );

  useEffect(() => {
    void store.enqueue(attachmentId);
  }, [store, attachmentId]);

  return useSyncExternalStoreWithSelector(
    store.subscribe,
    getAttachmentUrlState,
    getAttachmentUrlState,
    selectorFor_useAttachmentUrl,
    shallow
  );
}

/**
 * @internal
 */
function useAttachmentUrlSuspense_withRoomContext(
  RoomContext: Context<OpaqueRoom | null>,
  attachmentId: string
) {
  const room = useRoom_withRoomContext(RoomContext);
  const { attachmentUrlsStore } = room[kInternal];

  const getAttachmentUrlState = useCallback(
    () => attachmentUrlsStore.getItemState(attachmentId),
    [attachmentUrlsStore, attachmentId]
  );
  const attachmentUrlState = getAttachmentUrlState();

  if (!attachmentUrlState || attachmentUrlState.isLoading) {
    throw attachmentUrlsStore.enqueue(attachmentId);
  }

  if (attachmentUrlState.error) {
    throw attachmentUrlState.error;
  }

  const state = useSyncExternalStore(
    attachmentUrlsStore.subscribe,
    getAttachmentUrlState,
    getAttachmentUrlState
  );
  assert(state !== undefined, "Unexpected missing state");
  assert(!state.isLoading, "Unexpected loading state");
  assert(!state.error, "Unexpected error state");
  return {
    isLoading: false,
    url: state.data,
    error: undefined,
  } as const;
}

/**
 * Returns a presigned URL for an attachment by its ID.
 *
 * @example
 * const { url } = useAttachmentUrl("at_xxx");
 */
function useAttachmentUrlSuspense(attachmentId: string) {
  return useAttachmentUrlSuspense_withRoomContext(
    GlobalRoomContext,
    attachmentId
  );
}

/**
 * @private For internal use only. Do not rely on this hook.
 */
function useRoomPermissions(roomId: string) {
  const client = useClient();
  const store = getRoomExtrasForClient(client).store;
  return useSignal(store.permissionHints.getPermissionForRoomΣ(roomId))
    ?.permissions;
}

/**
 * @private For internal use only. Do not rely on this hook.
 */
function useHasPermissionAccess(
  roomId: string,
  resource: PermissionResources,
  requiredAccess: RequiredAccessLevel
): boolean {
  const permissions = useRoomPermissions(roomId);
  const fallback = useSelfAccessFallback(roomId, resource, requiredAccess);

  return permissions !== undefined
    ? hasPermissionAccess(permissions, resource, requiredAccess)
    : fallback;
}

// Permission hints come from REST; until they exist, fall back to scopes from
// the room connection (same optimistic defaults as isStorageWritable).
function useSelfAccessFallback(
  roomId: string,
  resource: PermissionResources,
  requiredAccess: RequiredAccessLevel
): boolean {
  const room = useRoom_withRoomContext(GlobalRoomContext, {
    allowOutsideRoom: true,
  });

  const subscribe = useCallback(
    (callback: () => void) => {
      if (room === null || room.id !== roomId) {
        return () => {};
      }

      return room.events.self.subscribe(callback);
    },
    [room, roomId]
  );

  const getSnapshot = useCallback(() => {
    const isCommentsResource =
      resource === "comments" ||
      resource === "comments:public" ||
      resource === "comments:private";

    if (room?.id === roomId) {
      const permissionMatrix = room[kInternal].getPermissionMatrix();
      if (permissionMatrix !== undefined) {
        return hasPermissionAccess(permissionMatrix, resource, requiredAccess);
      }
    }

    if (
      requiredAccess === "write" &&
      (isCommentsResource || resource === "storage")
    ) {
      return true;
    }

    return false;
  }, [resource, requiredAccess, room, roomId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Creates a RoomProvider and a set of typed hooks to use in your app. Note
 * that any RoomProvider created in this way does not need to be nested in
 * LiveblocksProvider, as it already has access to the client.
 */
export function createRoomContext<
  P extends JsonObject = DP,
  S extends LsonObject = DS,
  U extends BaseUserMeta = DU,
  E extends Json = DE,
  TM extends BaseMetadata = DTM,
  CM extends BaseMetadata = DCM,
  FM extends Json = Json,
  FMD extends Json = Json,
>(client: OpaqueClient): RoomContextBundle<P, S, U, E, TM, CM, FM, FMD> {
  type TRoom = Room<P, S, U, E, TM, CM, FM, FMD>;
  type TRoomBundle = RoomContextBundle<P, S, U, E, TM, CM, FM, FMD>;

  const BoundRoomContext = createContext<OpaqueRoom | null>(null);

  function RoomProvider_withImplicitLiveblocksProviderAndBoundRoomContext(
    props: RoomProviderProps<P, S>
  ) {
    // NOTE: Normally, nesting LiveblocksProvider is not allowed. This
    // factory-bound version of the RoomProvider will create an implicit
    // LiveblocksProvider. This means that if an end user nests this
    // RoomProvider under a LiveblocksProvider context, that would be an error.
    // However, we'll allow that nesting only in this specific situation, and
    // only because this wrapper will keep the Liveblocks context and the Room
    // context consistent internally.
    return (
      <LiveblocksProviderWithClient client={client} allowNesting>
        {/* @ts-expect-error {...props} is the same type as props */}
        <RoomProvider {...props} BoundRoomContext={BoundRoomContext} />
      </LiveblocksProviderWithClient>
    );
  }

  function useRoom_withBoundRoomContext(
    ...args: Parameters<typeof useRoom<P, S, U, E, TM, CM>>
  ) {
    return useRoom_withRoomContext<P, S, U, E, TM, CM>(
      BoundRoomContext,
      ...args
    );
  }

  function useStatus_withBoundRoomContext() {
    return useStatus_withRoomContext(BoundRoomContext);
  }

  function useBroadcastEvent_withBoundRoomContext() {
    return useBroadcastEvent_withRoomContext<E>(BoundRoomContext);
  }

  function useOthersListener_withBoundRoomContext(
    ...args: Parameters<typeof useOthersListener<P, U>>
  ) {
    return useOthersListener_withRoomContext<P, U>(BoundRoomContext, ...args);
  }

  function useLostConnectionListener_withBoundRoomContext(
    ...args: Parameters<typeof useLostConnectionListener>
  ) {
    return useLostConnectionListener_withRoomContext(BoundRoomContext, ...args);
  }

  function useEventListener_withBoundRoomContext(
    ...args: Parameters<typeof useEventListener<P, U, E>>
  ) {
    return useEventListener_withRoomContext<P, U, E>(BoundRoomContext, ...args);
  }

  function useMarkThreadAsRead_withBoundRoomContext() {
    return useMarkThreadAsRead_withRoomContext(BoundRoomContext);
  }

  function useHistory_withBoundRoomContext() {
    return useHistory_withRoomContext(BoundRoomContext);
  }

  function useUndo_withBoundRoomContext() {
    return useUndo_withRoomContext(BoundRoomContext);
  }

  function useRedo_withBoundRoomContext() {
    return useRedo_withRoomContext(BoundRoomContext);
  }

  function useCanUndo_withBoundRoomContext() {
    return useCanUndo_withRoomContext(BoundRoomContext);
  }

  function useCanRedo_withBoundRoomContext() {
    return useCanRedo_withRoomContext(BoundRoomContext);
  }

  function useStorageRoot_withBoundRoomContext() {
    return useStorageRoot_withRoomContext<S>(BoundRoomContext);
  }

  function useStorage_withBoundRoomContext<T>(
    ...args: Parameters<typeof useStorage<S, T>>
  ) {
    return useStorage_withRoomContext<S, T>(BoundRoomContext, ...args);
  }

  function useStorageSuspense_withBoundRoomContext<T>(
    ...args: Parameters<typeof useStorageSuspense<S, T>>
  ) {
    return useStorageSuspense_withRoomContext<S, T>(BoundRoomContext, ...args);
  }

  function useSelf_withBoundRoomContext<T>(
    ...args: Parameters<typeof useSelf<P, U, T>>
  ) {
    return useSelf_withRoomContext<P, U, T>(BoundRoomContext, ...args);
  }

  function useMyPresence_withBoundRoomContext() {
    return useMyPresence_withRoomContext<P>(BoundRoomContext);
  }

  function useUpdateMyPresence_withBoundRoomContext() {
    return useUpdateMyPresence_withRoomContext<P>(BoundRoomContext);
  }

  function useOthers_withBoundRoomContext<T>(
    ...args: Parameters<typeof useOthers<P, U, T>>
  ) {
    return useOthers_withRoomContext<P, U, T>(BoundRoomContext, ...args);
  }

  function useOthersMapped_withBoundRoomContext<T>(
    ...args: Parameters<typeof useOthersMapped<P, U, T>>
  ) {
    return useOthersMapped_withRoomContext<P, U, T>(BoundRoomContext, ...args);
  }

  function useOthersConnectionIds_withBoundRoomContext() {
    return useOthersConnectionIds_withRoomContext(BoundRoomContext);
  }

  function useOther_withBoundRoomContext<T>(
    ...args: Parameters<typeof useOther<P, U, T>>
  ) {
    return useOther_withRoomContext<P, U, T>(BoundRoomContext, ...args);
  }

  function useSelfSuspense_withBoundRoomContext<T>(
    ...args: Parameters<typeof useSelfSuspense<P, U, T>>
  ) {
    return useSelfSuspense_withRoomContext<P, U, T>(BoundRoomContext, ...args);
  }

  function useOthersSuspense_withBoundRoomContext<T>(
    ...args: Parameters<typeof useOthersSuspense<P, U, T>>
  ) {
    return useOthersSuspense_withRoomContext<P, U, T>(
      BoundRoomContext,
      ...args
    );
  }

  function useOthersMappedSuspense_withBoundRoomContext<T>(
    ...args: Parameters<typeof useOthersMappedSuspense<P, U, T>>
  ) {
    return useOthersMappedSuspense_withRoomContext<P, U, T>(
      BoundRoomContext,
      ...args
    );
  }

  function useOthersConnectionIdsSuspense_withBoundRoomContext() {
    return useOthersConnectionIdsSuspense_withRoomContext(BoundRoomContext);
  }

  function useOtherSuspense_withBoundRoomContext<T>(
    ...args: Parameters<typeof useOtherSuspense<P, U, T>>
  ) {
    return useOtherSuspense_withRoomContext<P, U, T>(BoundRoomContext, ...args);
  }

  function useMutation_withBoundRoomContext<
    F extends (context: MutationContext<P, S, U>, ...args: any[]) => any,
  >(...args: Parameters<typeof useMutation<P, S, U, E, TM, CM, F>>) {
    return useMutation_withRoomContext<P, S, U, E, TM, CM, F>(
      BoundRoomContext,
      ...args
    );
  }

  function useThreads_withBoundRoomContext(
    ...args: Parameters<typeof useThreads<TM, CM>>
  ) {
    return useThreads_withRoomContext<TM, CM>(BoundRoomContext, ...args);
  }

  function useCreateThread_withBoundRoomContext() {
    return useCreateThread_withRoomContext<TM, CM>(BoundRoomContext);
  }

  function useDeleteThread_withBoundRoomContext() {
    return useDeleteThread_withRoomContext(BoundRoomContext);
  }

  function useEditThreadMetadata_withBoundRoomContext() {
    return useEditThreadMetadata_withRoomContext<TM>(BoundRoomContext);
  }

  function useMarkThreadAsResolved_withBoundRoomContext() {
    return useMarkThreadAsResolved_withRoomContext(BoundRoomContext);
  }

  function useMarkThreadAsUnresolved_withBoundRoomContext() {
    return useMarkThreadAsUnresolved_withRoomContext(BoundRoomContext);
  }

  function useThreadsSuspense_withBoundRoomContext(
    ...args: Parameters<typeof useThreadsSuspense<TM, CM>>
  ) {
    return useThreadsSuspense_withRoomContext<TM, CM>(
      BoundRoomContext,
      ...args
    );
  }

  function useSubscribeToThread_withBoundRoomContext() {
    return useSubscribeToThread_withRoomContext(BoundRoomContext);
  }

  function useUnsubscribeFromThread_withBoundRoomContext() {
    return useUnsubscribeFromThread_withRoomContext(BoundRoomContext);
  }

  function useCreateComment_withBoundRoomContext() {
    return useCreateComment_withRoomContext<CM>(BoundRoomContext);
  }

  function useEditComment_withBoundRoomContext() {
    return useEditComment_withRoomContext<CM>(BoundRoomContext);
  }

  function useEditCommentMetadata_withBoundRoomContext() {
    return useEditCommentMetadata_withRoomContext<CM>(BoundRoomContext);
  }

  function useDeleteComment_withBoundRoomContext() {
    return useDeleteComment_withRoomContext(BoundRoomContext);
  }

  function useAddReaction_withBoundRoomContext() {
    return useAddReaction_withRoomContext(BoundRoomContext);
  }

  function useRemoveReaction_withBoundRoomContext() {
    return useRemoveReaction_withRoomContext(BoundRoomContext);
  }

  function useThreadSubscription_withBoundRoomContext(
    ...args: Parameters<typeof useThreadSubscription>
  ) {
    return useThreadSubscription_withRoomContext(BoundRoomContext, ...args);
  }

  function useAttachmentUrl_withBoundRoomContext(
    ...args: Parameters<typeof useAttachmentUrl>
  ) {
    return useAttachmentUrl_withRoomContext(BoundRoomContext, ...args);
  }

  function useAttachmentUrlSuspense_withBoundRoomContext(
    ...args: Parameters<typeof useAttachmentUrlSuspense>
  ) {
    return useAttachmentUrlSuspense_withRoomContext(BoundRoomContext, ...args);
  }

  function useSearchComments_withBoundRoomContext(
    ...args: Parameters<typeof useSearchComments<TM>>
  ) {
    return useSearchComments_withRoomContext<TM>(BoundRoomContext, ...args);
  }

  function useHistoryVersions_withBoundRoomContext() {
    return useHistoryVersions_withRoomContext(BoundRoomContext);
  }

  function useHistoryVersionsSuspense_withBoundRoomContext() {
    return useHistoryVersionsSuspense_withRoomContext(BoundRoomContext);
  }

  function useHistoryVersionStorageData_withBoundRoomContext(
    ...args: Parameters<typeof useHistoryVersionStorageData>
  ) {
    return useHistoryVersionStorageData_withRoomContext(
      BoundRoomContext,
      ...args
    );
  }

  function useHistoryVersionYjsData_withBoundRoomContext(
    ...args: Parameters<typeof useHistoryVersionYjsData>
  ) {
    return useHistoryVersionYjsData_withRoomContext(BoundRoomContext, ...args);
  }

  function useDeleteHistoryVersion_withBoundRoomContext() {
    return useDeleteHistoryVersion_withRoomContext(BoundRoomContext);
  }

  function useRestoreToStorageVersion_withBoundRoomContext(
    ...args: Parameters<typeof useRestoreToStorageVersion>
  ) {
    return useRestoreToStorageVersion_withRoomContext(BoundRoomContext, ...args);
  }

  function useRoomSubscriptionSettings_withBoundRoomContext() {
    return useRoomSubscriptionSettings_withRoomContext(BoundRoomContext);
  }

  function useRoomSubscriptionSettingsSuspense_withBoundRoomContext() {
    return useRoomSubscriptionSettingsSuspense_withRoomContext(
      BoundRoomContext
    );
  }

  function useUpdateRoomSubscriptionSettings_withBoundRoomContext() {
    return useUpdateRoomSubscriptionSettings_withRoomContext(BoundRoomContext);
  }

  function useFeeds_withBoundRoomContext(...args: Parameters<typeof useFeeds>) {
    return useFeeds_withRoomContext(BoundRoomContext, ...args);
  }

  function useFeedMessages_withBoundRoomContext(
    ...args: Parameters<typeof useFeedMessages>
  ) {
    return useFeedMessages_withRoomContext(BoundRoomContext, ...args);
  }

  function useFeedsSuspense_withBoundRoomContext(
    ...args: Parameters<typeof useFeedsSuspense>
  ) {
    return useFeedsSuspense_withRoomContext(BoundRoomContext, ...args);
  }

  function useFeedMessagesSuspense_withBoundRoomContext(
    ...args: Parameters<typeof useFeedMessagesSuspense>
  ) {
    return useFeedMessagesSuspense_withRoomContext(BoundRoomContext, ...args);
  }

  function useCreateFeed_withBoundRoomContext() {
    return useCreateFeed_withRoomContext(BoundRoomContext);
  }

  function useDeleteFeed_withBoundRoomContext() {
    return useDeleteFeed_withRoomContext(BoundRoomContext);
  }

  function useUpdateFeedMetadata_withBoundRoomContext() {
    return useUpdateFeedMetadata_withRoomContext(BoundRoomContext);
  }

  function useCreateFeedMessage_withBoundRoomContext() {
    return useCreateFeedMessage_withRoomContext(BoundRoomContext);
  }

  function useDeleteFeedMessage_withBoundRoomContext() {
    return useDeleteFeedMessage_withRoomContext(BoundRoomContext);
  }

  function useUpdateFeedMessage_withBoundRoomContext() {
    return useUpdateFeedMessage_withRoomContext(BoundRoomContext);
  }

  const shared = createSharedContext(client as Client<U>);
  const bundle: RoomContextBundle<P, S, U, E, TM, CM, FM, FMD> = {
    RoomContext: BoundRoomContext as Context<TRoom | null>,
    RoomProvider:
      RoomProvider_withImplicitLiveblocksProviderAndBoundRoomContext,

    // prettier-ignore
    useRoom: useRoom_withBoundRoomContext as TRoomBundle["useRoom"],
    // prettier-ignore
    useStatus: useStatus_withBoundRoomContext as TRoomBundle["useStatus"],
    // prettier-ignore
    useBroadcastEvent: useBroadcastEvent_withBoundRoomContext as TRoomBundle["useBroadcastEvent"],
    // prettier-ignore
    useOthersListener: useOthersListener_withBoundRoomContext as TRoomBundle["useOthersListener"],
    // prettier-ignore
    useLostConnectionListener: useLostConnectionListener_withBoundRoomContext as TRoomBundle["useLostConnectionListener"],
    // prettier-ignore
    useEventListener: useEventListener_withBoundRoomContext as TRoomBundle["useEventListener"],

    // prettier-ignore
    useHistory: useHistory_withBoundRoomContext as TRoomBundle["useHistory"],
    // prettier-ignore
    useUndo: useUndo_withBoundRoomContext as TRoomBundle["useUndo"],
    // prettier-ignore
    useRedo: useRedo_withBoundRoomContext as TRoomBundle["useRedo"],
    // prettier-ignore
    useCanUndo: useCanUndo_withBoundRoomContext as TRoomBundle["useCanUndo"],
    // prettier-ignore
    useCanRedo: useCanRedo_withBoundRoomContext as TRoomBundle["useCanRedo"],

    // prettier-ignore
    useStorageRoot: useStorageRoot_withBoundRoomContext as TRoomBundle["useStorageRoot"],
    // prettier-ignore
    useStorage: useStorage_withBoundRoomContext as TRoomBundle["useStorage"],
    // prettier-ignore
    useMutation: useMutation_withBoundRoomContext as TRoomBundle["useMutation"],

    // prettier-ignore
    useSelf: useSelf_withBoundRoomContext as TRoomBundle["useSelf"],
    // prettier-ignore
    useMyPresence: useMyPresence_withBoundRoomContext as TRoomBundle["useMyPresence"],
    // prettier-ignore
    useUpdateMyPresence: useUpdateMyPresence_withBoundRoomContext as TRoomBundle["useUpdateMyPresence"],
    // prettier-ignore
    useOthers: useOthers_withBoundRoomContext as TRoomBundle["useOthers"],
    // prettier-ignore
    useOthersMapped: useOthersMapped_withBoundRoomContext as TRoomBundle["useOthersMapped"],
    // prettier-ignore
    useOthersConnectionIds: useOthersConnectionIds_withBoundRoomContext as TRoomBundle["useOthersConnectionIds"],
    // prettier-ignore
    useOther: useOther_withBoundRoomContext as TRoomBundle["useOther"],

    // prettier-ignore
    useThreads: useThreads_withBoundRoomContext as TRoomBundle["useThreads"],
    // prettier-ignore
    useFeeds: useFeeds_withBoundRoomContext as TRoomBundle["useFeeds"],
    // prettier-ignore
    useFeedMessages: useFeedMessages_withBoundRoomContext as TRoomBundle["useFeedMessages"],
    // prettier-ignore
    useCreateFeed: useCreateFeed_withBoundRoomContext as TRoomBundle["useCreateFeed"],
    // prettier-ignore
    useDeleteFeed: useDeleteFeed_withBoundRoomContext as TRoomBundle["useDeleteFeed"],
    // prettier-ignore
    useUpdateFeedMetadata: useUpdateFeedMetadata_withBoundRoomContext as TRoomBundle["useUpdateFeedMetadata"],
    // prettier-ignore
    useCreateFeedMessage: useCreateFeedMessage_withBoundRoomContext as TRoomBundle["useCreateFeedMessage"],
    // prettier-ignore
    useDeleteFeedMessage: useDeleteFeedMessage_withBoundRoomContext as TRoomBundle["useDeleteFeedMessage"],
    // prettier-ignore
    useUpdateFeedMessage: useUpdateFeedMessage_withBoundRoomContext as TRoomBundle["useUpdateFeedMessage"],
    // prettier-ignore
    useCreateThread: useCreateThread_withBoundRoomContext as TRoomBundle["useCreateThread"],
    // prettier-ignore
    useDeleteThread: useDeleteThread_withBoundRoomContext as TRoomBundle["useDeleteThread"],
    // prettier-ignore
    useEditThreadMetadata: useEditThreadMetadata_withBoundRoomContext as TRoomBundle["useEditThreadMetadata"],
    // prettier-ignore
    useMarkThreadAsResolved: useMarkThreadAsResolved_withBoundRoomContext as TRoomBundle["useMarkThreadAsResolved"],
    // prettier-ignore
    useMarkThreadAsUnresolved: useMarkThreadAsUnresolved_withBoundRoomContext as TRoomBundle["useMarkThreadAsUnresolved"],
    // prettier-ignore
    useSubscribeToThread: useSubscribeToThread_withBoundRoomContext as TRoomBundle["useSubscribeToThread"],
    // prettier-ignore
    useUnsubscribeFromThread: useUnsubscribeFromThread_withBoundRoomContext as TRoomBundle["useUnsubscribeFromThread"],
    // prettier-ignore
    useCreateComment: useCreateComment_withBoundRoomContext as TRoomBundle["useCreateComment"],
    // prettier-ignore
    useEditComment: useEditComment_withBoundRoomContext as TRoomBundle["useEditComment"],
    // prettier-ignore
    useEditCommentMetadata: useEditCommentMetadata_withBoundRoomContext as TRoomBundle["useEditCommentMetadata"],
    // prettier-ignore
    useDeleteComment: useDeleteComment_withBoundRoomContext as TRoomBundle["useDeleteComment"],
    // prettier-ignore
    useAddReaction: useAddReaction_withBoundRoomContext as TRoomBundle["useAddReaction"],
    // prettier-ignore
    useRemoveReaction: useRemoveReaction_withBoundRoomContext as TRoomBundle["useRemoveReaction"],
    // prettier-ignore
    useMarkThreadAsRead: useMarkThreadAsRead_withBoundRoomContext as TRoomBundle["useMarkThreadAsRead"],
    // prettier-ignore
    useThreadSubscription: useThreadSubscription_withBoundRoomContext as TRoomBundle["useThreadSubscription"],
    // prettier-ignore
    useAttachmentUrl: useAttachmentUrl_withBoundRoomContext as TRoomBundle["useAttachmentUrl"],
    // prettier-ignore
    useSearchComments: useSearchComments_withBoundRoomContext as TRoomBundle["useSearchComments"],

    // prettier-ignore
    useHistoryVersions: useHistoryVersions_withBoundRoomContext as TRoomBundle["useHistoryVersions"],
    // prettier-ignore
    useHistoryVersionData: useHistoryVersionYjsData_withBoundRoomContext as TRoomBundle["useHistoryVersionData"],
    // prettier-ignore
    useHistoryVersionStorageData: useHistoryVersionStorageData_withBoundRoomContext as TRoomBundle["useHistoryVersionStorageData"],
    useHistoryVersionYjsData:
      useHistoryVersionYjsData_withBoundRoomContext as TRoomBundle["useHistoryVersionYjsData"],
    // prettier-ignore
    useDeleteHistoryVersion: useDeleteHistoryVersion_withBoundRoomContext as TRoomBundle["useDeleteHistoryVersion"],
    // prettier-ignore
    useRestoreToStorageVersion: useRestoreToStorageVersion_withBoundRoomContext as TRoomBundle["useRestoreToStorageVersion"],

    // prettier-ignore
    useRoomSubscriptionSettings: useRoomSubscriptionSettings_withBoundRoomContext as TRoomBundle["useRoomSubscriptionSettings"],
    // prettier-ignore
    useUpdateRoomSubscriptionSettings: useUpdateRoomSubscriptionSettings_withBoundRoomContext as TRoomBundle["useUpdateRoomSubscriptionSettings"],

    ...shared.classic,

    suspense: {
      RoomContext: BoundRoomContext as Context<TRoom | null>,
      RoomProvider:
        RoomProvider_withImplicitLiveblocksProviderAndBoundRoomContext,

      // prettier-ignore
      useRoom: useRoom_withBoundRoomContext as TRoomBundle["suspense"]["useRoom"],
      // prettier-ignore
      useStatus: useStatus_withBoundRoomContext as TRoomBundle["suspense"]["useStatus"],
      // prettier-ignore
      useBroadcastEvent: useBroadcastEvent_withBoundRoomContext as TRoomBundle["suspense"]["useBroadcastEvent"],
      // prettier-ignore
      useOthersListener: useOthersListener_withBoundRoomContext as TRoomBundle["suspense"]["useOthersListener"],
      // prettier-ignore
      useLostConnectionListener: useLostConnectionListener_withBoundRoomContext as TRoomBundle["suspense"]["useLostConnectionListener"],
      // prettier-ignore
      useEventListener: useEventListener_withBoundRoomContext as TRoomBundle["suspense"]["useEventListener"],

      // prettier-ignore
      useHistory: useHistory_withBoundRoomContext as TRoomBundle["suspense"]["useHistory"],
      // prettier-ignore
      useUndo: useUndo_withBoundRoomContext as TRoomBundle["suspense"]["useUndo"],
      // prettier-ignore
      useRedo: useRedo_withBoundRoomContext as TRoomBundle["suspense"]["useRedo"],
      // prettier-ignore
      useCanUndo: useCanUndo_withBoundRoomContext as TRoomBundle["suspense"]["useCanUndo"],
      // prettier-ignore
      useCanRedo: useCanRedo_withBoundRoomContext as TRoomBundle["suspense"]["useCanRedo"],

      // prettier-ignore
      useStorageRoot: useStorageRoot_withBoundRoomContext as TRoomBundle["suspense"]["useStorageRoot"],
      // prettier-ignore
      useStorage: useStorageSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useStorage"],
      // prettier-ignore
      useMutation: useMutation_withBoundRoomContext as TRoomBundle["suspense"]["useMutation"],

      // prettier-ignore
      useSelf: useSelfSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useSelf"],
      // prettier-ignore
      useMyPresence: useMyPresence_withBoundRoomContext as TRoomBundle["suspense"]["useMyPresence"],
      // prettier-ignore
      useUpdateMyPresence: useUpdateMyPresence_withBoundRoomContext as TRoomBundle["suspense"]["useUpdateMyPresence"],
      // prettier-ignore
      useOthers: useOthersSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useOthers"],
      // prettier-ignore
      useOthersMapped: useOthersMappedSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useOthersMapped"],
      // prettier-ignore
      useOthersConnectionIds: useOthersConnectionIdsSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useOthersConnectionIds"],
      // prettier-ignore
      useOther: useOtherSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useOther"],

      // prettier-ignore
      useThreads: useThreadsSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useThreads"],
      // prettier-ignore
      useFeeds: useFeedsSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useFeeds"],
      // prettier-ignore
      useFeedMessages: useFeedMessagesSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useFeedMessages"],
      // prettier-ignore
      useCreateFeed: useCreateFeed_withBoundRoomContext as TRoomBundle["suspense"]["useCreateFeed"],
      // prettier-ignore
      useDeleteFeed: useDeleteFeed_withBoundRoomContext as TRoomBundle["suspense"]["useDeleteFeed"],
      // prettier-ignore
      useUpdateFeedMetadata: useUpdateFeedMetadata_withBoundRoomContext as TRoomBundle["suspense"]["useUpdateFeedMetadata"],
      // prettier-ignore
      useCreateFeedMessage: useCreateFeedMessage_withBoundRoomContext as TRoomBundle["suspense"]["useCreateFeedMessage"],
      // prettier-ignore
      useDeleteFeedMessage: useDeleteFeedMessage_withBoundRoomContext as TRoomBundle["suspense"]["useDeleteFeedMessage"],
      // prettier-ignore
      useUpdateFeedMessage: useUpdateFeedMessage_withBoundRoomContext as TRoomBundle["suspense"]["useUpdateFeedMessage"],
      // prettier-ignore
      useCreateThread: useCreateThread_withBoundRoomContext as TRoomBundle["suspense"]["useCreateThread"],
      // prettier-ignore
      useDeleteThread: useDeleteThread_withBoundRoomContext as TRoomBundle["suspense"]["useDeleteThread"],
      // prettier-ignore
      useEditThreadMetadata: useEditThreadMetadata_withBoundRoomContext as TRoomBundle["suspense"]["useEditThreadMetadata"],
      // prettier-ignore
      useMarkThreadAsResolved: useMarkThreadAsResolved_withBoundRoomContext as TRoomBundle["suspense"]["useMarkThreadAsResolved"],
      // prettier-ignore
      useMarkThreadAsUnresolved: useMarkThreadAsUnresolved_withBoundRoomContext as TRoomBundle["suspense"]["useMarkThreadAsUnresolved"],
      // prettier-ignore
      useSubscribeToThread: useSubscribeToThread_withBoundRoomContext as TRoomBundle["suspense"]["useSubscribeToThread"],
      // prettier-ignore
      useUnsubscribeFromThread: useUnsubscribeFromThread_withBoundRoomContext as TRoomBundle["suspense"]["useUnsubscribeFromThread"],
      // prettier-ignore
      useCreateComment: useCreateComment_withBoundRoomContext as TRoomBundle["suspense"]["useCreateComment"],
      // prettier-ignore
      useEditComment: useEditComment_withBoundRoomContext as TRoomBundle["suspense"]["useEditComment"],
      // prettier-ignore
      useEditCommentMetadata: useEditCommentMetadata_withBoundRoomContext as TRoomBundle["suspense"]["useEditCommentMetadata"],
      // prettier-ignore
      useDeleteComment: useDeleteComment_withBoundRoomContext as TRoomBundle["suspense"]["useDeleteComment"],
      // prettier-ignore
      useAddReaction: useAddReaction_withBoundRoomContext as TRoomBundle["suspense"]["useAddReaction"],
      // prettier-ignore
      useRemoveReaction: useRemoveReaction_withBoundRoomContext as TRoomBundle["suspense"]["useRemoveReaction"],
      // prettier-ignore
      useMarkThreadAsRead: useMarkThreadAsRead_withBoundRoomContext as TRoomBundle["suspense"]["useMarkThreadAsRead"],
      // prettier-ignore
      useThreadSubscription: useThreadSubscription_withBoundRoomContext as TRoomBundle["suspense"]["useThreadSubscription"],
      // prettier-ignore
      useAttachmentUrl: useAttachmentUrlSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useAttachmentUrl"],

      // prettier-ignore
      useHistoryVersions: useHistoryVersionsSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useHistoryVersions"],

      // prettier-ignore
      useRoomSubscriptionSettings: useRoomSubscriptionSettingsSuspense_withBoundRoomContext as TRoomBundle["suspense"]["useRoomSubscriptionSettings"],
      // prettier-ignore
      useUpdateRoomSubscriptionSettings: useUpdateRoomSubscriptionSettings_withBoundRoomContext as TRoomBundle["suspense"]["useUpdateRoomSubscriptionSettings"],

      // No Suspense version: useSearchComments
      // No Suspense version: useHistoryVersionData

      ...shared.suspense,
    },
  };

  return Object.defineProperty(bundle, kInternal, {
    enumerable: false,
  });
}

type TypedBundle = RoomContextBundle<DP, DS, DU, DE, DTM, DCM, DFM, DFMD>;

/**
 * Makes a Room available in the component hierarchy below.
 * Joins the room when the component is mounted, and automatically leaves
 * the room when the component is unmounted.
 */
const _RoomProvider: TypedBundle["RoomProvider"] = RoomProvider;

/**
 * Returns a callback that lets you broadcast custom events to other users in the room
 *
 * @example
 * const broadcast = useBroadcastEvent();
 *
 * broadcast({ type: "CUSTOM_EVENT", data: { x: 0, y: 0 } });
 */
const _useBroadcastEvent: TypedBundle["useBroadcastEvent"] = useBroadcastEvent;

/**
 * Get informed when users enter or leave the room, as an event.
 *
 * @example
 * useOthersListener({ type, user, others }) => {
 *   if (type === 'enter') {
 *     // `user` has joined the room
 *   } else if (type === 'leave') {
 *     // `user` has left the room
 *   }
 * })
 */
const _useOthersListener: TypedBundle["useOthersListener"] = useOthersListener;

/**
 * Returns the Room of the nearest RoomProvider above in the React component
 * tree.
 */
const _useRoom: TypedBundle["useRoom"] = useRoom;

/**
 * Returns whether the hook is called within a RoomProvider context.
 *
 * @example
 * const isInsideRoom = useIsInsideRoom();
 */
const _useIsInsideRoom: TypedBundle["useIsInsideRoom"] = useIsInsideRoom;

/**
 * Returns a function that adds a reaction from a comment.
 *
 * @example
 * const addReaction = useAddReaction();
 * addReaction({ threadId: "th_xxx", commentId: "cm_xxx", emoji: "👍" })
 */
const _useAddReaction: TypedBundle["useAddReaction"] = useAddReaction;

/**
 * Create a callback function that lets you mutate Liveblocks state.
 *
 * The first argument that gets passed into your callback will be
 * a "mutation context", which exposes the following:
 *
 *   - `storage` - The mutable Storage root.
 *                 You can mutate any Live structures with this, for example:
 *                 `storage.get('layers').get('layer1').set('fill', 'red')`
 *
 *   - `setMyPresence` - Call this with a new (partial) Presence value.
 *
 *   - `self` - A read-only version of the latest self, if you need it to
 *              compute the next state.
 *
 *   - `others` - A read-only version of the latest others list, if you
 *                need it to compute the next state.
 *
 * useMutation is like React's useCallback, except that the first argument
 * that gets passed into your callback will be a "mutation context".
 *
 * If you want get access to the immutable root somewhere in your mutation,
 * you can use `storage.toJSON()`.
 *
 * @example
 * const fillLayers = useMutation(
 *   ({ storage }, color: Color) => {
 *     ...
 *   },
 *   [],
 * );
 *
 * fillLayers('red');
 *
 * const deleteLayers = useMutation(
 *   ({ storage }) => {
 *     ...
 *   },
 *   [],
 * );
 *
 * deleteLayers();
 */
const _useMutation: TypedBundle["useMutation"] = useMutation;

/**
 * Returns a function that creates a thread with an initial comment, and optionally some metadata.
 *
 * @example
 * const createThread = useCreateThread();
 * createThread({ body: {}, metadata: {} });
 */
const _useCreateThread: TypedBundle["useCreateThread"] = useCreateThread;

/**
 * Returns a function that deletes a thread and its associated comments.
 * Only the thread creator can delete a thread, it will throw otherwise.
 *
 * @example
 * const deleteThread = useDeleteThread();
 * deleteThread("th_xxx");
 */
const _useDeleteThread: TypedBundle["useDeleteThread"] = useDeleteThread;

/**
 * Returns a function that edits a thread's metadata.
 * To delete an existing metadata property, set its value to `null`.
 *
 * @example
 * const editThreadMetadata = useEditThreadMetadata();
 * editThreadMetadata({ threadId: "th_xxx", metadata: {} })
 */
const _useEditThreadMetadata: TypedBundle["useEditThreadMetadata"] =
  useEditThreadMetadata;

/**
 * Returns a function that adds a comment to a thread.
 *
 * @example
 * const createComment = useCreateComment();
 * createComment({ threadId: "th_xxx", body: {} });
 */
const _useCreateComment: TypedBundle["useCreateComment"] = useCreateComment;

/**
 * Returns a function that edits a comment.
 *
 * @example
 * const editComment = useEditComment()
 * editComment({ threadId: "th_xxx", commentId: "cm_xxx", body: {} })
 */
const _useEditComment: TypedBundle["useEditComment"] = useEditComment;

/**
 * Returns a function that edits a comment's metadata.
 * To delete an existing metadata property, set its value to `null`.
 *
 * @example
 * const editCommentMetadata = useEditCommentMetadata();
 * editCommentMetadata({ threadId: "th_xxx", commentId: "cm_xxx", metadata: { tag: "important", externalId: 1234  } })
 */
const _useEditCommentMetadata: TypedBundle["useEditCommentMetadata"] =
  useEditCommentMetadata;

/**
 * useEventListener is a React hook that allows you to respond to events broadcast
 * by other users in the room.
 *
 * The `user` argument will indicate which `User` instance sent the message.
 * This will be equal to one of the others in the room, but it can be `null`
 * in case this event was broadcasted from the server.
 *
 * @example
 * useEventListener(({ event, user, connectionId }) => {
 * //                         ^^^^ Will be Client A
 *   if (event.type === "CUSTOM_EVENT") {
 *     // Do something
 *   }
 * });
 */
const _useEventListener: TypedBundle["useEventListener"] = useEventListener;

/**
 * Returns the presence of the current user of the current room, and a function to update it.
 * It is different from the setState function returned by the useState hook from
 * You don't need to pass the full presence object to update it.
 *
 * @example
 * const [myPresence, updateMyPresence] = useMyPresence();
 * updateMyPresence({ x: 0 });
 * updateMyPresence({ y: 0 });
 *
 * // At the next render, "myPresence" will be equal to "{ x: 0, y: 0 }"
 */
const _useMyPresence: TypedBundle["useMyPresence"] = useMyPresence;

/**
 * Related to useOthers(), but optimized for selecting only "subsets" of
 * others. This is useful for performance reasons in particular, because
 * selecting only a subset of users also means limiting the number of
 * re-renders that will be triggered.
 *
 * @example
 * const avatars = useOthersMapped(user => user.info.avatar);
 * //    ^^^^^^^
 * //    { connectionId: number; data: string }[]
 *
 * The selector function you pass to useOthersMapped() is called an "item
 * selector", and operates on a single user at a time. If you provide an
 * (optional) "item comparison" function, it will be used to compare each
 * item pairwise.
 *
 * For example, to select multiple properties:
 *
 * @example
 * const avatarsAndCursors = useOthersMapped(
 *   user => [u.info.avatar, u.presence.cursor],
 *   shallow,  // 👈
 * );
 */
const _useOthersMapped: TypedBundle["useOthersMapped"] = useOthersMapped;

/**
 * Related to useOthers(), but optimized for selecting only "subsets" of
 * others. This is useful for performance reasons in particular, because
 * selecting only a subset of users also means limiting the number of
 * re-renders that will be triggered.
 *
 * @example
 * const avatars = useOthersMapped(user => user.info.avatar);
 * //    ^^^^^^^
 * //    { connectionId: number; data: string }[]
 *
 * The selector function you pass to useOthersMapped() is called an "item
 * selector", and operates on a single user at a time. If you provide an
 * (optional) "item comparison" function, it will be used to compare each
 * item pairwise.
 *
 * For example, to select multiple properties:
 *
 * @example
 * const avatarsAndCursors = useOthersMapped(
 *   user => [u.info.avatar, u.presence.cursor],
 *   shallow,  // 👈
 * );
 */
const _useOthersMappedSuspense: TypedBundle["suspense"]["useOthersMapped"] =
  useOthersMappedSuspense;

/**
 * Returns the threads within the current room.
 *
 * @example
 * const { threads, error, isLoading } = useThreads();
 */
const _useThreads: TypedBundle["useThreads"] = useThreads;

/**
 * Returns feeds for the current room.
 *
 * @example
 * const { feeds, error, isLoading } = useFeeds();
 */
const _useFeeds: TypedBundle["useFeeds"] = useFeeds;

/**
 * Returns messages for a specific feed in the current room.
 *
 * @example
 * const { messages, error, isLoading } = useFeedMessages("feed-id");
 */
const _useFeedMessages: TypedBundle["useFeedMessages"] = useFeedMessages;

/**
 * Returns feeds for the current room.
 *
 * @example
 * const { feeds } = useFeeds();
 */
const _useFeedsSuspense: TypedBundle["suspense"]["useFeeds"] = useFeedsSuspense;

/**
 * Returns messages for a specific feed in the current room.
 *
 * @example
 * const { messages } = useFeedMessages("feed-id");
 */
const _useFeedMessagesSuspense: TypedBundle["suspense"]["useFeedMessages"] =
  useFeedMessagesSuspense;

/**
 * Returns the result of searching comments by text in the current room. The result includes the id and the plain text content of the matched comments along with the parent thread id of the comment.
 *
 * @example
 * const { results, error, isLoading } = useSearchComments({ query: { text: "hello"} });
 */
const _useSearchComments: TypedBundle["useSearchComments"] = useSearchComments;

/**
 * Returns the threads within the current room.
 *
 * @example
 * const { threads } = useThreads();
 */
const _useThreadsSuspense: TypedBundle["suspense"]["useThreads"] =
  useThreadsSuspense;

/**
 * Returns the user's subscription settings for the current room
 * and a function to update them.
 *
 * @example
 * const [{ settings }, updateSettings] = useRoomSubscriptionSettings();
 */
const _useRoomSubscriptionSettings: TypedBundle["useRoomSubscriptionSettings"] =
  useRoomSubscriptionSettings;

/**
 * Returns the user's subscription settings for the current room
 * and a function to update them.
 *
 * @example
 * const [{ settings }, updateSettings] = useRoomSubscriptionSettings();
 */
const _useRoomSubscriptionSettingsSuspense: TypedBundle["suspense"]["useRoomSubscriptionSettings"] =
  useRoomSubscriptionSettingsSuspense;

/**
 * (Private beta) Returns a history of versions of the current room.
 *
 * @example
 * const { versions, error, isLoading } = useHistoryVersions();
 */
const _useHistoryVersions: TypedBundle["useHistoryVersions"] =
  useHistoryVersions;

/**
 * (Private beta) Returns a history of versions of the current room.
 *
 * @example
 * const { versions } = useHistoryVersions();
 */
const _useHistoryVersionsSuspense: TypedBundle["suspense"]["useHistoryVersions"] =
  useHistoryVersionsSuspense;

/**
 * Given a connection ID (as obtained by using `useOthersConnectionIds`), you
 * can call this selector deep down in your component stack to only have the
 * component re-render if properties for this particular user change.
 *
 * @example
 * // Returns only the selected values re-renders whenever that selection changes)
 * const { x, y } = useOther(2, user => user.presence.cursor);
 */
const _useOther: TypedBundle["useOther"] = useOther;

/**
 * Returns an array with information about all the users currently connected in
 * the room (except yourself).
 *
 * @example
 * const others = useOthers();
 *
 * // Example to map all cursors in JSX
 * return (
 *   <>
 *     {others.map((user) => {
 *        if (user.presence.cursor == null) {
 *          return null;
 *        }
 *        return <Cursor key={user.connectionId} cursor={user.presence.cursor} />
 *      })}
 *   </>
 * )
 */
function _useOthers(): readonly User<DP, DU>[];
/**
 * Extract arbitrary data based on all the users currently connected in the
 * room (except yourself).
 *
 * The selector function will get re-evaluated any time a user enters or
 * leaves the room, as well as whenever their presence data changes.
 *
 * The component that uses this hook will automatically re-render if your
 * selector function returns a different value from its previous run.
 *
 * By default `useOthers()` uses strict `===` to check for equality. Take
 * extra care when returning a computed object or list, for example when you
 * return the result of a .map() or .filter() call from the selector. In
 * those cases, you'll probably want to use a `shallow` comparison check.
 *
 * @example
 * const avatars = useOthers(users => users.map(u => u.info.avatar), shallow);
 * const cursors = useOthers(users => users.map(u => u.presence.cursor), shallow);
 * const someoneIsTyping = useOthers(users => users.some(u => u.presence.isTyping));
 *
 */
function _useOthers<T>(
  selector: (others: readonly User<DP, DU>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function _useOthers(...args: any[]) {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- internal wrapper
  return useOthers(...(args as []));
}

/**
 * Given a connection ID (as obtained by using `useOthersConnectionIds`), you
 * can call this selector deep down in your component stack to only have the
 * component re-render if properties for this particular user change.
 *
 * @example
 * // Returns only the selected values re-renders whenever that selection changes)
 * const { x, y } = useOther(2, user => user.presence.cursor);
 */
const _useOtherSuspense: TypedBundle["suspense"]["useOther"] = useOtherSuspense;

/**
 * Returns an array with information about all the users currently connected in
 * the room (except yourself).
 *
 * @example
 * const others = useOthers();
 *
 * // Example to map all cursors in JSX
 * return (
 *   <>
 *     {others.map((user) => {
 *        if (user.presence.cursor == null) {
 *          return null;
 *        }
 *        return <Cursor key={user.connectionId} cursor={user.presence.cursor} />
 *      })}
 *   </>
 * )
 */
function _useOthersSuspense(): readonly User<DP, DU>[];
/**
 * Extract arbitrary data based on all the users currently connected in the
 * room (except yourself).
 *
 * The selector function will get re-evaluated any time a user enters or
 * leaves the room, as well as whenever their presence data changes.
 *
 * The component that uses this hook will automatically re-render if your
 * selector function returns a different value from its previous run.
 *
 * By default `useOthers()` uses strict `===` to check for equality. Take
 * extra care when returning a computed object or list, for example when you
 * return the result of a .map() or .filter() call from the selector. In
 * those cases, you'll probably want to use a `shallow` comparison check.
 *
 * @example
 * const avatars = useOthers(users => users.map(u => u.info.avatar), shallow);
 * const cursors = useOthers(users => users.map(u => u.presence.cursor), shallow);
 * const someoneIsTyping = useOthers(users => users.some(u => u.presence.isTyping));
 *
 */
function _useOthersSuspense<T>(
  selector: (others: readonly User<DP, DU>[]) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function _useOthersSuspense(...args: any[]) {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- internal wrapper
  return useOthersSuspense(...(args as []));
}

/**
 * Extract arbitrary data from the Liveblocks Storage state, using an
 * arbitrary selector function.
 *
 * The selector function will get re-evaluated any time something changes in
 * Storage. The value returned by your selector function will also be the
 * value returned by the hook.
 *
 * The `root` value that gets passed to your selector function is
 * a immutable/readonly version of your Liveblocks storage root.
 *
 * The component that uses this hook will automatically re-render if the
 * returned value changes.
 *
 * By default `useStorage()` uses strict `===` to check for equality. Take
 * extra care when returning a computed object or list, for example when you
 * return the result of a .map() or .filter() call from the selector. In
 * those cases, you'll probably want to use a `shallow` comparison check.
 */
const _useStorage: TypedBundle["useStorage"] = useStorage;

/**
 * Extract arbitrary data from the Liveblocks Storage state, using an
 * arbitrary selector function.
 *
 * The selector function will get re-evaluated any time something changes in
 * Storage. The value returned by your selector function will also be the
 * value returned by the hook.
 *
 * The `root` value that gets passed to your selector function is
 * a immutable/readonly version of your Liveblocks storage root.
 *
 * The component that uses this hook will automatically re-render if the
 * returned value changes.
 *
 * By default `useStorage()` uses strict `===` to check for equality. Take
 * extra care when returning a computed object or list, for example when you
 * return the result of a .map() or .filter() call from the selector. In
 * those cases, you'll probably want to use a `shallow` comparison check.
 */
const _useStorageSuspense: TypedBundle["suspense"]["useStorage"] =
  useStorageSuspense;

/**
 * Gets the current user once it is connected to the room.
 *
 * @example
 * const me = useSelf();
 * if (me !== null) {
 *   const { x, y } = me.presence.cursor;
 * }
 */
function _useSelf(): User<DP, DU> | null;
/**
 * Extract arbitrary data based on the current user.
 *
 * The selector function will get re-evaluated any time your presence data
 * changes.
 *
 * The component that uses this hook will automatically re-render if your
 * selector function returns a different value from its previous run.
 *
 * By default `useSelf()` uses strict `===` to check for equality. Take extra
 * care when returning a computed object or list, for example when you return
 * the result of a .map() or .filter() call from the selector. In those
 * cases, you'll probably want to use a `shallow` comparison check.
 *
 * Will return `null` while Liveblocks isn't connected to a room yet.
 *
 * @example
 * const cursor = useSelf(me => me.presence.cursor);
 * if (cursor !== null) {
 *   const { x, y } = cursor;
 * }
 *
 */
function _useSelf<T>(
  selector: (me: User<DP, DU>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T | null;
function _useSelf(...args: any[]) {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- internal wrapper
  return useSelf(...(args as []));
}

/**
 * Gets the current user once it is connected to the room.
 *
 * @example
 * const me = useSelf();
 * const { x, y } = me.presence.cursor;
 */
function _useSelfSuspense(): User<DP, DU>;
/**
 * Extract arbitrary data based on the current user.
 *
 * The selector function will get re-evaluated any time your presence data
 * changes.
 *
 * The component that uses this hook will automatically re-render if your
 * selector function returns a different value from its previous run.
 *
 * By default `useSelf()` uses strict `===` to check for equality. Take extra
 * care when returning a computed object or list, for example when you return
 * the result of a .map() or .filter() call from the selector. In those
 * cases, you'll probably want to use a `shallow` comparison check.
 *
 * @example
 * const cursor = useSelf(me => me.presence.cursor);
 * const { x, y } = cursor;
 *
 */
function _useSelfSuspense<T>(
  selector: (me: User<DP, DU>) => T,
  isEqual?: (prev: T, curr: T) => boolean
): T;
function _useSelfSuspense(...args: any[]) {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- internal wrapper
  return useSelfSuspense(...(args as []));
}

/**
 * Returns the mutable (!) Storage root. This hook exists for
 * backward-compatible reasons.
 *
 * @example
 * const [root] = useStorageRoot();
 */
const _useStorageRoot: TypedBundle["useStorageRoot"] = useStorageRoot;

/**
 * useUpdateMyPresence is similar to useMyPresence but it only returns the function to update the current user presence.
 * If you don't use the current user presence in your component, but you need to update it (e.g. live cursor), it's better to use useUpdateMyPresence to avoid unnecessary renders.
 *
 * @example
 * const updateMyPresence = useUpdateMyPresence();
 * updateMyPresence({ x: 0 });
 * updateMyPresence({ y: 0 });
 *
 * // At the next render, the presence of the current user will be equal to "{ x: 0, y: 0 }"
 */
const _useUpdateMyPresence: TypedBundle["useUpdateMyPresence"] =
  useUpdateMyPresence;

export {
  _RoomProvider as RoomProvider,
  _useAddReaction as useAddReaction,
  useAddRoomCommentReaction,
  useAttachmentUrl,
  useAttachmentUrlSuspense,
  _useBroadcastEvent as useBroadcastEvent,
  useCanRedo,
  useCanUndo,
  _useCreateComment as useCreateComment,
  useCreateFeed,
  useCreateFeedMessage,
  useCreateRoomComment,
  useCreateRoomThread,
  useCreateTextMention,
  _useCreateThread as useCreateThread,
  useDeleteComment,
  useDeleteFeed,
  useDeleteFeedMessage,
  useDeleteHistoryVersion,
  useDeleteRoomComment,
  useDeleteRoomThread,
  useDeleteTextMention,
  _useDeleteThread as useDeleteThread,
  _useEditComment as useEditComment,
  _useEditCommentMetadata as useEditCommentMetadata,
  useEditRoomComment,
  useEditRoomCommentMetadata,
  useEditRoomThreadMetadata,
  _useEditThreadMetadata as useEditThreadMetadata,
  _useEventListener as useEventListener,
  _useFeedMessages as useFeedMessages,
  _useFeedMessagesSuspense as useFeedMessagesSuspense,
  _useFeeds as useFeeds,
  _useFeedsSuspense as useFeedsSuspense,
  useHasPermissionAccess,
  useHistory,
  useHistoryVersionData,
  _useHistoryVersions as useHistoryVersions,
  _useHistoryVersionsSuspense as useHistoryVersionsSuspense,
  useHistoryVersionStorageData,
  useHistoryVersionYjsData,
  _useIsInsideRoom as useIsInsideRoom,
  useLostConnectionListener,
  useMarkRoomThreadAsRead,
  useMarkRoomThreadAsResolved,
  useMarkRoomThreadAsUnresolved,
  useMarkThreadAsRead,
  useMarkThreadAsResolved,
  useMarkThreadAsUnresolved,
  useMentionSuggestionsCache,
  _useMutation as useMutation,
  _useMyPresence as useMyPresence,
  _useOther as useOther,
  _useOthers as useOthers,
  useOthersConnectionIds,
  useOthersConnectionIdsSuspense,
  _useOthersListener as useOthersListener,
  _useOthersMapped as useOthersMapped,
  _useOthersMappedSuspense as useOthersMappedSuspense,
  _useOthersSuspense as useOthersSuspense,
  _useOtherSuspense as useOtherSuspense,
  useRedo,
  useRemoveReaction,
  useRemoveRoomCommentReaction,
  useReportTextEditor,
  useResolveMentionSuggestions,
  useRestoreToStorageVersion,
  _useRoom as useRoom,
  useRoomAttachmentUrl,
  useRoomPermissions,
  _useRoomSubscriptionSettings as useRoomSubscriptionSettings,
  _useRoomSubscriptionSettingsSuspense as useRoomSubscriptionSettingsSuspense,
  useRoomThreadSubscription,
  _useSearchComments as useSearchComments,
  _useSelf as useSelf,
  _useSelfSuspense as useSelfSuspense,
  useStatus,
  _useStorage as useStorage,
  _useStorageRoot as useStorageRoot,
  _useStorageSuspense as useStorageSuspense,
  useSubscribeToRoomThread,
  useSubscribeToThread,
  _useThreads as useThreads,
  _useThreadsSuspense as useThreadsSuspense,
  useThreadSubscription,
  useUndo,
  useUnsubscribeFromRoomThread,
  useUnsubscribeFromThread,
  useUpdateFeedMessage,
  useUpdateFeedMetadata,
  _useUpdateMyPresence as useUpdateMyPresence,
  useUpdateRoomSubscriptionSettings,
  useYjsProvider,
};
